import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { ReactComponent as Reload } from "../../../../assets/icons/svg/Relode.svg";
import { ReactComponent as Delete } from "../../../../assets/icons/svg/Cluva/delete.svg";
import { ReactComponent as CluvaLogo } from "../../../../assets/icons/svg/Cluva/Logo-with-text.svg";
import { ReactComponent as Home } from "../../../../assets/icons/svg/home.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as UrlArrow } from "../../../../assets/icons/svg/url_arrow.svg";
import { ReactComponent as EditPreview } from "../../../../assets/icons/svg/Edit_Preview.svg";
import AvatarIcon from "../../../../assets/images/profile-avatar.svg";
import Button from "../../../ui/buttons";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";
import ScreenshotManager from "../../../ui/screenshot-screenrecord/Screenshot/ScreenshotManager";
import { ScreenshotSubmission } from "../../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import ScreenshotPreviewComponent from "../../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import IframeErrorPage from "./IframeErrorPage";
import ScreenRecordingPreview, {
  RecordingSubmission,
} from "../../../ui/screenshot-screenrecord/screen-recording/ScreenRecordingPreview";
import ViewModeDropdown from "../../../ui/ViewModeDropdown";
import { API_URLS, BASE_URL, markTourGuideDone, getTourGuideStatus, TOUR_GUIDE_KEYS } from "../../../../api/urls";
import { EnhancedTokenManager } from "../../../../utils/tokenManager";
import GenieEffect from "../../../ui/GenieEffect";
import AppTour, { AppTourStep } from "../../../ui/AppTour";
import ShareFeedbackPopup from "../../../ui/ShareFeedbackPopup";
import { shareFeedbackWithDeveloper } from "../../../../api/api-utils";
import {
  compressImage,
  needsCompression,
  getImageSizeMB,
} from "../../../../utils/imageCompression";
import {
  compressVideo,
  needsCompression as needsVideoCompression,
  getCompressionStats,
} from "../../../../utils/videoCompression";
import UploadQueueManager from "../../../../utils/uploadQueueManager";
import UploadQueueIndicator from "../../../ui/UploadQueueIndicator";
import {
  FeedbackItem,
  FeedbackWorkspaceProps,
  ScreenshotSubmissionEnhanced,
  RecordingSubmissionEnhanced,
  CreateFeedbackApiResponse,
  RefreshTokenResponse,
  AuthTokens,
} from "../../../../types/feedback.types";
import { generateVideoThumbnail } from "../../../../utils/feedbackUtils";
import {
  getTechnicalContext,
  getConsoleErrors,
  getFailedRequests,
} from "../../../../utils/technicalContext";

declare global {
  interface Window {
    __emptyContainerRef?: HTMLElement;
  }
}

const DynamicTooltip: React.FC<{
  label: string | React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom";
  wrapperClassName?: string;
}> = ({ label, children, side = "bottom", wrapperClassName = "" }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [alignStyle, setAlignStyle] = useState<React.CSSProperties>({
    left: "50%",
    transform: "translateX(-50%)",
  });

  const handleMouseEnter = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const labelStr = typeof label === "string" ? label : "tooltip";
      const estimatedWidth = labelStr.length * 7 + 16;
      const mid = rect.left + rect.width / 2;

      if (mid + estimatedWidth / 2 > vw - 8) {
        setAlignStyle({ right: 0, left: "auto", transform: "none" });
      } else if (mid - estimatedWidth / 2 < 8) {
        setAlignStyle({ left: 0, transform: "none" });
      } else {
        setAlignStyle({ left: "50%", transform: "translateX(-50%)" });
      }
    }
    setShow(true);
  };

  const posClass = side === "bottom" ? "top-full mt-1" : "-top-8";

  return (
    <div
      ref={wrapperRef}
      className={`relative ${wrapperClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <div
        className={`absolute ${posClass} bg-gray-800 text-white text-xs px-2 py-2 rounded-[4px] whitespace-nowrap pointer-events-none transition-opacity duration-300 z-50 ${show ? "opacity-100" : "opacity-0"}`}
        style={{ fontFamily: DEFAULT_FONT_FAMILY, ...alignStyle }}
      >
        {label}
      </div>
    </div>
  );
};

/**
 * Enhanced API Service Class
 */
class ApiService {
  private static isRefreshing = false;
  private static failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
  }> = [];

  private static processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = EnhancedTokenManager.getRefreshToken();

    if (!refreshToken) {
      console.log("No refresh token available");
      return false;
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      console.log("Attempting to refresh access token...");
      const response = await fetch(API_URLS.REFRESH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      console.log("Refresh token response status:", response.status);

      if (response.ok) {
        const data: RefreshTokenResponse = await response.json();
        console.log("Token refresh successful");

        const newTokens: AuthTokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token || refreshToken,
          token_type: data.token_type || "bearer",
        };

        EnhancedTokenManager.setTokens(newTokens);
        this.processQueue(null, data.access_token);
        return true;
      } else {
        const errorData = await response.json();
        console.error("Refresh token failed:", errorData);
        EnhancedTokenManager.performLogout();
        this.processQueue(errorData, null);
        return false;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.processQueue(error, null);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  // Update the makeAuthenticatedRequest method in the ApiService class
  static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const accessToken = EnhancedTokenManager.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available. Please log in again.");
    }

    console.log(`Making authenticated request to: ${url}`);

    // Check if body is FormData to avoid setting Content-Type
    const isFormData = options.body instanceof FormData;

    const headers = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    };

    let response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`Initial response status: ${response.status}`);

    if (response.status === 401) {
      console.log("Received 401, attempting token refresh...");

      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        const newAccessToken = EnhancedTokenManager.getAccessToken();
        console.log(
          "Token refreshed successfully, retrying original request...",
        );

        response = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        });

        console.log(`Retry response status: ${response.status}`);
      } else {
        throw new Error("Authentication failed. Please log in again.");
      }
    }

    return response;
  }

  /**
   * Creates a feedback inside a card when URL is submitted
   */
  static async createFeedbackInsideCard(
    cardId: number,
    workspaceId: number,
    url: string,
    name?: string,
  ): Promise<CreateFeedbackApiResponse | null> {
    try {
      const apiUrl = API_URLS.CREATE_FEEDBACK_INSIDE_CARD(cardId, workspaceId);

      console.log("🚀 Creating feedback inside card:", {
        cardId,
        workspaceId,
        url,
        name,
        apiUrl,
      });

      const response = await this.makeAuthenticatedRequest(apiUrl, {
        method: "POST",
        body: JSON.stringify([
          {
            url: url,
            name: name || `Feedback for ${url}`,
            workspace_id: workspaceId,
          },
        ]),
      });

      console.log("🚀 Create feedback response status:", response.status);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `HTTP ${response.status}: ${errorText}`;
          }
          console.error("🚨 API Error Response:", errorText);
        } catch (e) {
          // If we can't read the error text, use the default message
        }

        if (response.status === 401) {
          errorMessage =
            "Authentication failed. Please check your access token.";
        } else if (response.status === 403) {
          errorMessage =
            "Access denied. You do not have permission to create feedback.";
        } else if (response.status === 404) {
          errorMessage = "Card not found. Please check the card ID.";
        } else if (response.status === 422) {
          errorMessage =
            "Invalid request data. Please check the required fields.";
        } else if (response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }

        throw new Error(errorMessage);
      }

      const apiResponseArray: CreateFeedbackApiResponse[] =
        await response.json();
      console.log("✅ Feedback created successfully:", apiResponseArray);

      const feedbackObject = apiResponseArray[0];

      if (!feedbackObject || !feedbackObject.id) {
        throw new Error("Invalid response: No feedback object or ID found");
      }

      console.log("✅ Extracted feedback ID:", feedbackObject.id);
      return feedbackObject;
    } catch (error) {
      console.error("🚨 Error creating feedback:", error);
      throw error;
    }
  }

  /**
   * Creates a feedback entry for a specific screenshot or recording
   */
  static async createFeedbackForItem(
    cardId: number,
    workspaceId: number,
    url: string,
    itemType: "screenshot" | "recording",
    itemId: string,
    preservedName?: string, // Add optional parameter for preserved name
  ): Promise<CreateFeedbackApiResponse | null> {
    // Use preserved name if provided, otherwise generate default name
    const name =
      preservedName ||
      `${
        itemType.charAt(0).toUpperCase() + itemType.slice(1)
      } Feedback - ${itemId}`;

    try {
      return await this.createFeedbackInsideCard(
        cardId,
        workspaceId,
        url,
        name,
      );
    } catch (error) {
      console.error(`🚨 Error creating feedback for ${itemType}:`, error);
      throw error;
    }
  }

  /**
   * Upload message/feedback text
   */
  /**
   * Upload message/feedback text with proper FormData handling
   */
  static async uploadMessage(
    message: string,
    feedbackId: string | number,
  ): Promise<any> {
    try {
      const cleanFeedbackId = String(feedbackId).trim();

      console.log("💬 Uploading message:", {
        messageLength: message.length,
        feedbackId: cleanFeedbackId,
        messagePreview:
          message.substring(0, 100) + (message.length > 100 ? "..." : ""),
      });

      const formData = new FormData();
      formData.append("message", message);
      formData.append("feedback_id", cleanFeedbackId);

      // Debug FormData contents
      console.log("💬 FormData contents:");
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        console.log(`  ${key}: ${value}`);
      });

      // Use makeAuthenticatedRequest but override headers to remove Content-Type
      // so browser can set multipart/form-data with proper boundary
      const response = await this.makeAuthenticatedRequest(
        API_URLS.UPLOAD_MESSAGE,
        {
          method: "POST",
          body: formData,
          headers: {
            // Don't set Content-Type - let browser handle it for FormData
            // Only Authorization will be set by makeAuthenticatedRequest
          },
        },
      );

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `HTTP ${response.status}: ${errorText}`;
          }
        } catch (e) {
          // If we can't read the error text, use the default message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Message uploaded successfully:", result);
      return result;
    } catch (error) {
      console.error("🚨 Error uploading message:", error);
      throw error;
    }
  }

  /**
   * Upload video thumbnail to database
   */
  static async uploadVideoThumbnail(
    thumbnailDataUrl: string,
    feedbackId: string | number,
  ): Promise<any> {
    try {
      const cleanFeedbackId = String(feedbackId).trim();

      console.log("🖼️ Uploading video thumbnail:", {
        feedbackId: cleanFeedbackId,
        thumbnailSize: thumbnailDataUrl.length,
      });

      // Convert data URL to blob
      const response = await fetch(thumbnailDataUrl);
      const blob = await response.blob();

      // Create PNG blob with proper MIME type
      const pngBlob = new Blob([blob], { type: "image/png" });

      // Create FormData
      const formData = new FormData();
      const fileName = `thumbnail_${cleanFeedbackId}_${Date.now()}.png`;
      formData.append("file", pngBlob, fileName);
      formData.append("feedback_id", cleanFeedbackId);

      // Debug FormData contents
      console.log("🖼️ Thumbnail FormData contents:");
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(
            `  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`,
          );
        } else {
          console.log(`  ${key}: ${value}`);
        }
      });

      const uploadResponse = await this.makeAuthenticatedRequest(
        API_URLS.UPLOAD_VIDEO_THUMBNAIL(cleanFeedbackId),
        {
          method: "POST",
          body: formData,
          headers: {
            // Don't set Content-Type - let browser handle it for FormData
            // Only Authorization will be set by makeAuthenticatedRequest
          },
        },
      );

      if (!uploadResponse.ok) {
        let errorMessage = `HTTP ${uploadResponse.status}: ${uploadResponse.statusText}`;
        try {
          const errorText = await uploadResponse.text();
          if (errorText) {
            errorMessage = `HTTP ${uploadResponse.status}: ${errorText}`;
          }
        } catch (e) {
          // If we can't read the error text, use the default message
        }
        throw new Error(errorMessage);
      }

      const result = await uploadResponse.json();
      console.log("✅ Video thumbnail uploaded successfully:", result);
      return result;
    } catch (error) {
      console.error("🚨 Error uploading video thumbnail:", error);
      throw error;
    }
  }

  /**
   * Enhanced Upload file to feedback with comprehensive error handling and video support
   * FIXED: Proper FormData handling for video uploads with CORS and timeout fixes
   */
  static async uploadFile(
    file: File | Blob,
    fileName: string,
    fileType: "screenshot" | "recording" | "audio",
    feedbackId?: string,
  ): Promise<any> {
    try {
      // Validate inputs
      if (!file) {
        throw new Error("No file provided for upload");
      }

      if (file.size === 0) {
        throw new Error("File is empty");
      }

      const maxSize =
        fileType === "recording" ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
      }

      const formData = new FormData();

      // Handle file preparation based on type
      if (fileType === "screenshot") {
        const ensuredFileName = fileName.endsWith(".png")
          ? fileName
          : `${fileName}.png`;
        let processedBlob: Blob = file;

        if (file.type !== "image/png") {
          try {
            const img = new Image();
            const imageUrl = URL.createObjectURL(file);

            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = imageUrl;
            });

            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");

            if (ctx) {
              ctx.drawImage(img, 0, 0);
              processedBlob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                  (blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("PNG conversion failed"));
                  },
                  "image/png",
                  1.0,
                );
              });
            }

            URL.revokeObjectURL(imageUrl);
          } catch (convErr) {
            console.warn("PNG conversion failed:", convErr);
            processedBlob = new Blob([file], { type: "image/png" });
          }
        }

        const pngFile = new File([processedBlob], ensuredFileName, {
          type: "image/png",
          lastModified: Date.now(),
        });
        formData.append("file", pngFile, ensuredFileName);
      } else if (fileType === "audio") {
        const audioFile = new File([file], fileName, {
          type: file.type || "audio/mp3",
          lastModified: Date.now(),
        });
        formData.append("file", audioFile, fileName);
      } else if (fileType === "recording") {
        // ✅ CRITICAL FIX: Ensure we have a valid blob
        if (!file || file.size === 0) {
          console.error("❌ Invalid recording blob:", {
            hasFile: !!file,
            size: file?.size || 0,
            type: file?.type || "unknown",
          });
          throw new Error(
            "Recording blob is invalid or empty. Please try recording again.",
          );
        }

        let finalFile: File;
        if (file instanceof File) {
          finalFile = file;
        } else {
          const videoType = fileName.endsWith(".webm")
            ? "video/webm"
            : "video/mp4";
          finalFile = new File([file], fileName, {
            type: videoType,
            lastModified: Date.now(),
          });
        }

        console.log("📹 Prepared video file for upload:", {
          fileName: finalFile.name,
          size: finalFile.size,
          type: finalFile.type,
          sizeInMB: (finalFile.size / (1024 * 1024)).toFixed(2),
        });

        formData.append("file", finalFile);
      }

      formData.append("file_type", fileType);

      if (feedbackId) {
        const cleanFeedbackId = String(feedbackId).trim();
        formData.append("feedback_id", cleanFeedbackId);
      }

      const apiUrl =
        fileType === "audio"
          ? API_URLS.UPLOAD_FILE_VOICE
          : API_URLS.UPLOAD_FILE;

      console.log("🚀 Uploading file:", {
        fileName,
        fileType,
        fileSize: file.size,
        fileSizeInMB: (file.size / (1024 * 1024)).toFixed(2),
        endpoint: apiUrl,
        feedbackId: feedbackId || "none",
      });

      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      // ✅ KEY FIX: Use XMLHttpRequest for better timeout handling
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const startTime = Date.now();
        let lastProgressTime = startTime;
        let lastProgressLoaded = 0;

        // Track upload progress
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            const elapsed = (Date.now() - startTime) / 1000;
            const timeSinceLastProgress =
              (Date.now() - lastProgressTime) / 1000;
            const bytesProgress = event.loaded - lastProgressLoaded;
            const currentSpeed =
              bytesProgress / 1024 / 1024 / timeSinceLastProgress; // MB/s

            lastProgressTime = Date.now();
            lastProgressLoaded = event.loaded;

            // Log every 10% for videos, 25% for screenshots
            const logInterval = fileType === "recording" ? 10 : 25;
            if (Math.floor(percentComplete) % logInterval === 0) {
              console.log(
                `📤 Upload progress: ${percentComplete.toFixed(1)}% ` +
                  `(${(event.loaded / 1024 / 1024).toFixed(1)}/${(
                    event.total /
                    1024 /
                    1024
                  ).toFixed(1)} MB, ` +
                  `${currentSpeed.toFixed(2)} MB/s)`,
              );
            }
          }
        });

        // Handle successful completion
        xhr.addEventListener("load", () => {
          const duration = ((Date.now() - startTime) / 1000).toFixed(1);
          const speed = (
            file.size /
            1024 /
            1024 /
            parseFloat(duration)
          ).toFixed(2);

          console.log(`📊 Upload completed:`, {
            status: xhr.status,
            duration: `${duration}s`,
            speed: `${speed} MB/s`,
          });

          if (xhr.status >= 200 && xhr.status < 300) {
            console.log(`✅ Upload successful in ${duration}s`);

            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              resolve({ status: "uploaded", message: xhr.responseText });
            }
          } else if (xhr.status === 0) {
            // ✅ CRITICAL: Status 0 = connection closed by proxy/timeout
            console.warn(
              `⚠️ Upload connection closed after ${duration}s (status 0 = proxy timeout)`,
            );

            // For videos that uploaded for a reasonable time, assume success
            if (fileType === "recording" && parseFloat(duration) > 30) {
              console.log(
                `✅ Video upload ran for ${duration}s - likely succeeded on backend despite proxy timeout`,
              );
              resolve({
                status: "uploaded",
                message: "Upload completed (proxy timeout ignored)",
                duration: duration,
                note: "Backend likely processed successfully despite connection close",
              });
            } else {
              reject(
                new Error(
                  `Upload connection closed after ${duration}s (proxy timeout)`,
                ),
              );
            }
          } else {
            console.error(`❌ Upload failed with status ${xhr.status}`);
            let errorMessage = `HTTP ${xhr.status}: ${xhr.statusText}`;

            if (xhr.status === 413) {
              errorMessage = `File too large (${(
                file.size /
                (1024 * 1024)
              ).toFixed(2)}MB)`;
            } else if (xhr.status === 415) {
              errorMessage = `Unsupported file type`;
            }

            reject(new Error(errorMessage));
          }
        });

        // Handle network errors
        xhr.addEventListener("error", () => {
          const duration = ((Date.now() - startTime) / 1000).toFixed(1);
          console.error(`❌ Upload network error after ${duration}s`);

          // ✅ For videos that ran for a while, be optimistic
          if (fileType === "recording" && parseFloat(duration) > 30) {
            console.warn(
              `⚠️ Video upload error after ${duration}s - backend may have succeeded`,
            );

            // Wait a moment to let backend finish processing
            setTimeout(() => {
              console.log(
                "✅ Assuming video upload succeeded despite network error",
              );
              resolve({
                status: "uploaded",
                message: "Upload completed (network error ignored)",
                duration: duration,
                note: "Backend likely processed successfully",
              });
            }, 2000);
          } else {
            reject(new Error(`Network error during upload after ${duration}s`));
          }
        });

        // Set timeout - very long for videos
        xhr.timeout = fileType === "recording" ? 600000 : 60000; // 10 min vs 1 min

        xhr.addEventListener("timeout", () => {
          const duration = ((Date.now() - startTime) / 1000).toFixed(1);
          console.error(
            `❌ Upload timeout after ${duration}s (limit: ${
              xhr.timeout / 1000
            }s)`,
          );
          reject(
            new Error(
              `Upload timed out after ${duration}s. File may be too large or connection too slow.`,
            ),
          );
        });

        // Configure request
        xhr.open("POST", apiUrl);
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

        // Start upload
        console.log(`📤 Starting upload (timeout: ${xhr.timeout / 1000}s)...`);
        xhr.send(formData);
      });
    } catch (error) {
      console.error("🚨 Error in uploadFile:", error);

      const errorMessage = (error as Error).message;

      // User-friendly error messages
      if (errorMessage.includes("CORS")) {
        throw error;
      } else if (errorMessage.includes("NETWORK ERROR")) {
        throw error;
      } else if (
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("NetworkError")
      ) {
        throw new Error(
          "Network connection failed. Please check your internet connection and try again.",
        );
      } else if (
        errorMessage.includes("413") ||
        errorMessage.includes("too large")
      ) {
        throw new Error(
          `File too large (${(file.size / (1024 * 1024)).toFixed(
            2,
          )}MB). Please reduce file size and try again.`,
        );
      }

      throw error;
    }
  }

  static async uploadEnvironmentData(
    feedbackId: string | number,
    technicalContext: ReturnType<typeof getTechnicalContext>,
    consoleErrors: ReturnType<typeof getConsoleErrors>,
    failedRequests: ReturnType<typeof getFailedRequests>,
  ): Promise<void> {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) return;

      await fetch(API_URLS.FEEDBACK_ENVIRONMENT(feedbackId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          technical_context: technicalContext,
          console_errors: consoleErrors,
          failed_requests: failedRequests,
        }),
        keepalive: true,
      });
    } catch (error) {
      console.warn("⚠️ Environment data upload failed (non-critical):", error);
    }
  }
}

// Utility functions
const isValidHttpUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
};

const formatUrl = (url: string): string => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return "";
  return /^https?:\/\//i.test(trimmedUrl)
    ? trimmedUrl
    : `https://${trimmedUrl}`;
};

const formatDuration = (duration: number): string => {
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
/**
 * LocalStorage key for storing recently used URLs
 */
const RECENT_URLS_STORAGE_KEY = "feedback_recent_urls";
const MAX_RECENT_URLS = 10; // Store up to 10 recent URLs

/**
 * Get recently used URLs from localStorage
 */
const getRecentUrls = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_URLS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error reading recent URLs:", error);
  }
  return [];
};

/**
 * Save a URL to recent URLs list
 */
const saveRecentUrl = (url: string) => {
  try {
    const recentUrls = getRecentUrls();

    // Remove if already exists (to move it to top)
    const filtered = recentUrls.filter((u) => u !== url);

    // Add to beginning
    filtered.unshift(url);

    // Keep only MAX_RECENT_URLS
    const trimmed = filtered.slice(0, MAX_RECENT_URLS);

    localStorage.setItem(RECENT_URLS_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error("Error saving recent URL:", error);
  }
};

/**
 * Filter URLs based on search query
 */
const filterUrls = (urls: string[], query: string): string[] => {
  if (!query || query.trim() === "") {
    return urls.slice(0, 4); // Show first 4 if no query
  }

  const lowerQuery = query.toLowerCase();
  return urls
    .filter((url) => url.toLowerCase().includes(lowerQuery))
    .slice(0, 4); // Show max 4 suggestions
};

const FeedbackWorkspace: React.FC<FeedbackWorkspaceProps> = ({
  feedbackName,
  onBack,
  onSaveAndExit,
  onSaveDraft,
  onFirstScreenshotTaken,
  onMinimize,
  cardId,
  workspaceId,
  initialWorkspaceState,
  initialFeedbackItems,
}) => {
  // State management - Initialize with preserved state if available
  const [url, setUrl] = useState(initialWorkspaceState?.url || "");
  const [inputUrl, setInputUrl] = useState(initialWorkspaceState?.url || "");
  const [feedbackId, setFeedbackId] = useState<string | undefined>(
    initialWorkspaceState?.feedbackId || undefined,
  );
  const [iframeHistory, setIframeHistory] = useState<string[]>(
    initialWorkspaceState?.iframeHistory || [],
  );
  const [iframeHistoryIndex, setIframeHistoryIndex] = useState(
    initialWorkspaceState?.iframeHistoryIndex ?? -1,
  );
  const [showIframe, setShowIframe] = useState(
    initialWorkspaceState?.showIframe || false,
  );
  const [iframeKey, setIframeKey] = useState(0);
  const [isWorkspaceFullscreen, setIsWorkspaceFullscreen] = useState(false);
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [showFloatingTopBar, setShowFloatingTopBar] = useState(false);
  const [toolboxPos, setToolboxPos] = useState<{ left: number; top: number } | null>(null);
  const toolboxRef = useRef<HTMLDivElement>(null);
  const toolboxDragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const [showFloatingSidebar, setShowFloatingSidebar] = useState(false);
  const [hideSidebar, setHideSidebar] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [fsToast, setFsToast] = useState<{ message: string; visible: boolean; type: "success" | "error" }>({ message: "", visible: false, type: "success" });
  const fsToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const floatingSidebarRef = useRef<HTMLElement>(null);
  const [iframeLoadError, setIframeLoadError] = useState(false);
  const [iframeErrorType, setIframeErrorType] = useState<'network' | 'blocked' | 'timeout' | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [blockedFramework, setBlockedFramework] = useState<string>('');
  const iframeLoadedRef = useRef(false);
  const serverReachableRef = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Stores url/route/build_version received from the iframe via postMessage
  const iframeContextRef = useRef<{ url?: string; route?: string; build_version?: string }>({});
  const [screenshots, setScreenshots] = useState<
    ScreenshotSubmissionEnhanced[]
  >(initialWorkspaceState?.screenshots || []);
  const [recordings, setRecordings] = useState<RecordingSubmissionEnhanced[]>(
    initialWorkspaceState?.recordings || [],
  );

  // Loading states
  const [isCreatingFeedback, setIsCreatingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string>("");

  const [feedbackItemsOrder, setFeedbackItemsOrder] = useState<string[]>(
    initialWorkspaceState?.feedbackItemsOrder || [],
  );

  // Preview state
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const [loadingItems, setLoadingItems] = useState<
    {
      id: string;
      type: "screenshot" | "recording";
      timestamp: number;
      progress: number; // Track progress percentage
    }[]
  >([]);

  // State for editing screenshot
  const [editingScreenshot, setEditingScreenshot] =
    useState<ScreenshotSubmissionEnhanced | null>(null);
  const [showEditPreview, setShowEditPreview] = useState(false);

  // Add state to preserve the original feedback name
  const [originalFeedbackName, setOriginalFeedbackName] = useState<string>(
    initialWorkspaceState?.feedbackId ? feedbackName : "",
  );
  // Track if this is the first screenshot for this session
  const [isFirstScreenshot, setIsFirstScreenshot] = useState(true);

  const [isUrlLocked, setIsUrlLocked] = useState(
    initialWorkspaceState?.isUrlLocked ||
      (initialFeedbackItems && initialFeedbackItems.length > 0) ||
      false,
  );
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: "screenshot" | "recording";
    uniqueFeedbackId?: string;
  } | null>(null);

  // Success message state
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");

  // Track if any feedback items have been added
  const [hasAddedFeedback, setHasAddedFeedback] = useState(
    (initialFeedbackItems && initialFeedbackItems.length > 0) || false,
  );

  const sidebarItemsRef = useRef<HTMLDivElement | null>(null);
  const sidebarThumbRef = useRef<HTMLDivElement | null>(null);
  const [isShaking, setIsShaking] = useState(false);

  const handleSidebarScroll = () => {
    const el = sidebarItemsRef.current;
    const thumb = sidebarThumbRef.current;
    if (!el || !thumb) return;
    if (el.scrollHeight <= el.clientHeight) { thumb.style.display = "none"; return; }
    thumb.style.display = "block";
    const ratio = el.scrollTop / (el.scrollHeight - el.clientHeight);
    const trackH = el.clientHeight;
    const thumbH = Math.max((el.clientHeight / el.scrollHeight) * trackH, 24);
    thumb.style.height = `${thumbH}px`;
    thumb.style.top = `${ratio * (trackH - thumbH)}px`;
  };

  useEffect(() => { handleSidebarScroll(); });

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = sidebarItemsRef.current;
    const thumb = sidebarThumbRef.current;
    if (!el || !thumb) return;
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const trackH = el.clientHeight;
    const thumbH = thumb.offsetHeight;
    const scrollRange = el.scrollHeight - el.clientHeight;
    const thumbRange = trackH - thumbH;

    const onMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientY - startY;
      el.scrollTop = startScrollTop + (delta / thumbRange) * scrollRange;
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const prevFeedbackCountRef = useRef(0);

  const [isMuted, setIsMuted] = useState(() => {
    const savedMuteState = localStorage.getItem("screenshot_sound_muted");
    return savedMuteState === "true";
  });

  const [isAnyPreviewOpen, setIsAnyPreviewOpen] = useState(false);
  const [isIframeFocused, setIsIframeFocused] = useState(false);

  //state variables for URL autocomplete
  const [showUrlDropdown, setShowUrlDropdown] = useState(false);
  const [filteredUrls, setFilteredUrls] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showDeleteCardConfirm, setShowDeleteCardConfirm] = useState(false);
  const [isDeletingCard, setIsDeletingCard] = useState(false);

  const [editingRecording, setEditingRecording] =
    useState<RecordingSubmissionEnhanced | null>(null);
  const [showEditRecordingPreview, setShowEditRecordingPreview] =
    useState(false);
  const thumbnailAttemptsRef = useRef<Set<string>>(new Set());

  const [showAppTour, setShowAppTour] = useState(false);
  const [isTourStatusLoaded, setIsTourStatusLoaded] = useState(false);

  // Key scoped to the logged-in user so the "tour seen" flag survives logout/re-login
  const getTourStorageKey = () => {
    const userId = localStorage.getItem("current_user_id") || "anonymous";
    return `app_tour_completed_${userId}`;
  };

  const [hasSeenTour, setHasSeenTour] = useState(() => {
    // ✅ Check if THIS specific project has completed the tour
    try {
      const stored = localStorage.getItem("tour_completed_projects");
      if (stored) {
        const completed = JSON.parse(stored) as number[];
        const hasCompleted = completed.includes(cardId);
        console.log("🔍 Initial hasSeenTour check:", {
          cardId,
          hasCompleted,
          completedProjects: completed,
        });
        return hasCompleted;
      }
    } catch (error) {
      console.error("Error checking initial tour state:", error);
    }
    return false;
  });

  // Track which projects have seen the tour
  const [tourCompletedProjects, setTourCompletedProjects] = useState<
    Set<number>
  >(() => {
    const stored = localStorage.getItem("tour_completed_projects");
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as number[]; // ✅ Type assertion
        return new Set(parsed);
      } catch (error) {
        console.error("Error parsing tour_completed_projects:", error);
        return new Set();
      }
    }
    return new Set();
  });

  //  Share functionality states
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Create global event to notify all components when preview opens/closes
  useEffect(() => {
    if (showEditPreview || showEditRecordingPreview) {
      // Notify all components that preview is open
      window.dispatchEvent(new CustomEvent("preview-opened"));
    } else {
      // Notify all components that preview is closed
      window.dispatchEvent(new CustomEvent("preview-closed"));
    }
  }, [showEditPreview, showEditRecordingPreview]);

  // ✅ NEW: Listen to global preview events for new screenshots/recordings
  useEffect(() => {
    const handlePreviewOpened = () => {
      console.log("FeedbackWorkspace: Preview opened - disabling shortcuts");
      setIsAnyPreviewOpen(true);
    };

    const handlePreviewClosed = () => {
      console.log("FeedbackWorkspace: Preview closed - enabling shortcuts");
      setIsAnyPreviewOpen(false);
    };

    window.addEventListener("preview-opened", handlePreviewOpened);
    window.addEventListener("preview-closed", handlePreviewClosed);

    return () => {
      window.removeEventListener("preview-opened", handlePreviewOpened);
      window.removeEventListener("preview-closed", handlePreviewClosed);
    };
  }, []);

  const tourSteps: AppTourStep[] = [
    {
      target: '[data-tour="url-input"]',
      title: "Paste any web url to mirror here.",
      description:
        "Whether it's web app, your website, SaaS or any link you can paste and mirror here.",
      position: "bottom",
      highlightElement: true,
    },
    {
      target: '[data-tour="screenshot-tool"]',
      title: "Pick any tool to take screenshot or record",
      description:
        "Use these tools to capture screenshots or record your screen. They're always accessible at the bottom of the sidebar.",
      position: "right", // ✅ FIXED: Removed trailing space/comment
      highlightElement: true,
    },
    {
      target: '[data-tour="view-list"]',
      title: "View detailed list of all feedback collected.",
      description:
        "Click here to see all your feedback items organized in a detailed list view.",
      position: "bottom", // ✅ FIXED: Changed from "top" to "bottom"
      highlightElement: true,
    },
  ];

  useEffect(() => {
    if (initialFeedbackItems && initialFeedbackItems.length > 0) {
      console.log("🔄 FeedbackWorkspace - Restoring feedback items:", {
        totalItems: initialFeedbackItems.length,
        receivedOrder: initialFeedbackItems.map((item, idx) => ({
          position: idx + 1,
          type: item.type,
          id: item.id,
          timestamp: isNaN(item.timestamp) ? "invalid" : new Date(item.timestamp).toISOString(),
        })),
      });

      const restoredScreenshots: ScreenshotSubmissionEnhanced[] = [];
      const restoredRecordings: RecordingSubmissionEnhanced[] = [];
      const restoredOrder: string[] = [];

      // Process items in their original order
      initialFeedbackItems.forEach((item) => {
        const itemKey = `${item.type}-${item.id}`;
        restoredOrder.push(itemKey);

        if (item.type === "screenshot") {
          const screenshot: ScreenshotSubmissionEnhanced = {
            id: item.id,
            annotatedImage: item.annotatedImage || "",
            originalImage: item.originalImage || "",
            feedback: item.feedback || "",
            timestamp: item.timestamp,
            hasAudio: item.hasAudio || false,
            audioUrl: item.audioUrl,
            audioFilename: item.audioFilename,
            question: item.question,
            user: item.user,
            isSelected: item.isSelected || false,
            // ✅ CRITICAL: Preserve both IDs
            uniqueFeedbackId: item.uniqueFeedbackId || item.id,
            apiFeedbackId: item.apiFeedbackId, // ✅ ADD THIS LINE
          };
          restoredScreenshots.push(screenshot);
        } else if (item.type === "recording") {
          let recordingBlob = item.recordingBlob;

          if (!recordingBlob && item.dataUrl) {
            fetch(item.dataUrl)
              .then((response) => response.blob())
              .then((blob) => {
                console.log(
                  `📹 Restored recording blob for ${item.id}:`,
                  blob.size,
                  "bytes",
                );
                setRecordings((prevRecordings) =>
                  prevRecordings.map((recording) =>
                    recording.id === item.id
                      ? { ...recording, recordingBlob: blob }
                      : recording,
                  ),
                );
              })
              .catch((error) => {
                console.warn(
                  `Failed to restore recording blob for ${item.id}:`,
                  error,
                );
              });

            recordingBlob = new Blob([], { type: "video/mp4" });
          } else if (!recordingBlob) {
            recordingBlob = new Blob([], { type: "video/mp4" });
          }

          const recording: RecordingSubmissionEnhanced = {
            id: item.id,
            dataUrl: item.dataUrl || "",
            recordingBlob: recordingBlob,
            duration: item.duration || 0,
            feedback: item.feedback || "",
            timestamp: item.timestamp,
            hasAudio: item.hasAudio || false,
            question: item.question,
            user: item.user,
            isSelected: item.isSelected || false,
            // ✅ CRITICAL: Preserve both IDs and thumbnail
            uniqueFeedbackId: item.uniqueFeedbackId || item.id,
            apiFeedbackId: item.apiFeedbackId, // ✅ ADD THIS LINE
            thumbnailUrl: item.thumbnailUrl,
          };
          restoredRecordings.push(recording);
        }
      });

      console.log("✅ Restoring items with order:", {
        restoredOrder: restoredOrder,
        screenshots: restoredScreenshots.length,
        recordings: restoredRecordings.length,
        // ✅ Log preserved IDs for verification
        preservedIds: [
          ...restoredScreenshots.map((s) => ({
            type: "screenshot",
            id: s.id,
            uniqueFeedbackId: s.uniqueFeedbackId,
            apiFeedbackId: s.apiFeedbackId,
          })),
          ...restoredRecordings.map((r) => ({
            type: "recording",
            id: r.id,
            uniqueFeedbackId: r.uniqueFeedbackId,
            apiFeedbackId: r.apiFeedbackId,
          })),
        ],
      });

      // CRITICAL: Set order FIRST, then items
      setFeedbackItemsOrder(restoredOrder);
      setScreenshots(restoredScreenshots);
      setRecordings(restoredRecordings);

      console.log("✅ State updated - items restored with preserved IDs");
    }
  }, [initialFeedbackItems]);

  // Log when initialWorkspaceState is received
  useEffect(() => {
    if (initialWorkspaceState) {
      console.log("🔄 Restoring workspace state:", {
        ...initialWorkspaceState,
        screenshotsCount: initialWorkspaceState.screenshots?.length || 0,
        recordingsCount: initialWorkspaceState.recordings?.length || 0,
      });

      // Restore all workspace state
      if (initialWorkspaceState.url) {
        setUrl(initialWorkspaceState.url);
        setInputUrl(initialWorkspaceState.url);
      }

      if (initialWorkspaceState.feedbackId) {
        setFeedbackId(initialWorkspaceState.feedbackId);
      }

      if (initialWorkspaceState.iframeHistory) {
        setIframeHistory(initialWorkspaceState.iframeHistory);
      }

      if (typeof initialWorkspaceState.iframeHistoryIndex === "number") {
        setIframeHistoryIndex(initialWorkspaceState.iframeHistoryIndex);
      }

      if (typeof initialWorkspaceState.showIframe === "boolean") {
        setShowIframe(initialWorkspaceState.showIframe);
      }

      // CRITICAL: Restore URL lock state
      if (typeof initialWorkspaceState.isUrlLocked === "boolean") {
        setIsUrlLocked(initialWorkspaceState.isUrlLocked);
        console.log(
          "🔒 URL lock state restored:",
          initialWorkspaceState.isUrlLocked,
        );
      } else {
        // Fallback: Lock URL if we have feedback items
        const hasItems =
          (initialWorkspaceState.screenshots?.length || 0) > 0 ||
          (initialWorkspaceState.recordings?.length || 0) > 0;
        if (hasItems) {
          setIsUrlLocked(true);
          console.log("🔒 URL locked due to existing feedback items");
        }
      }

      if (initialWorkspaceState.feedbackItemsOrder) {
        setFeedbackItemsOrder(initialWorkspaceState.feedbackItemsOrder);
      }
    }
  }, [initialWorkspaceState]);

  // Debug log for feedbackId changes
  useEffect(() => {
    console.log("🆔 Feedback ID changed:", feedbackId);
  }, [feedbackId]);

  // Computed values
  const currentIframeUrl =
    iframeHistoryIndex >= 0 && iframeHistoryIndex < iframeHistory.length
      ? iframeHistory[iframeHistoryIndex]
      : null;

  const isFullScreen = showIframe && currentIframeUrl;

  // Pre-flight check: ask the backend whether the URL allows iframe embedding.
  // For localhost/private IPs the backend cannot reach the dev server, so skip the
  // check and load the iframe directly. For all external URLs we call the backend
  // which performs a HEAD request and inspects X-Frame-Options / CSP frame-ancestors.
  useEffect(() => {
    if (!currentIframeUrl) {
      setIframeLoadError(false);
      setIframeErrorType(null);
      setIframeLoading(false);
      setBlockedFramework('');
      return;
    }
    setIframeLoadError(false);
    setIframeErrorType(null);
    setIframeLoading(true);
    setBlockedFramework('');
    iframeLoadedRef.current = false;
    serverReachableRef.current = false;

    const isLocal = (() => {
      try {
        const { hostname } = new URL(currentIframeUrl);
        return (
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          hostname === '[::1]' ||
          hostname === '0.0.0.0' ||
          /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname)
        );
      } catch { return false; }
    })();

    // Localhost — backend cannot reach it; skip check and load iframe directly
    if (isLocal) {
      serverReachableRef.current = true;
      setIframeLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const accessToken = EnhancedTokenManager.getAccessToken();

    fetch(API_URLS.CHECK_IFRAME_HEADERS, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ url: currentIframeUrl }),
    })
      .then(res => res.json())
      .then((data: { canEmbed: boolean; reason: string | null }) => {
        if (cancelled) return;
        if (data.canEmbed) {
          serverReachableRef.current = true;
          setIframeLoading(false);
        } else {
          iframeLoadedRef.current = true;
          const errorType =
            data.reason === 'x-frame-options' || data.reason === 'csp-frame-ancestors'
              ? 'blocked'
              : data.reason === 'timeout'
              ? 'timeout'
              : 'network';
          setIframeErrorType(errorType);
          setIframeLoadError(true);
          setIframeLoading(false);
        }
      })
      .catch(err => {
        if (cancelled || err.name === 'AbortError') return;
        // Backend API itself failed — fall back to loading the iframe directly
        serverReachableRef.current = true;
        setIframeLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [currentIframeUrl, iframeKey]);

  // Sync iframe URL bar navigation into iframeContextRef
  useEffect(() => {
    if (!currentIframeUrl) return;
    try {
      const parsed = new URL(currentIframeUrl);
      iframeContextRef.current.url   = currentIframeUrl;
      iframeContextRef.current.route = parsed.pathname + parsed.search + parsed.hash;
    } catch {
      iframeContextRef.current.url   = currentIframeUrl;
      iframeContextRef.current.route = currentIframeUrl;
    }
  }, [currentIframeUrl]);

  // Background: try to discover build version from the app's common version endpoints
  // (package.json, version.json, meta.json). Only runs when a new origin is loaded.
  useEffect(() => {
    if (!currentIframeUrl || iframeContextRef.current.build_version) return;
    let cancelled = false;
    const discover = async () => {
      let origin: string;
      try { origin = new URL(currentIframeUrl).origin; } catch { return; }
      const candidates = [
        `${origin}/package.json`,
        `${origin}/version.json`,
        `${origin}/meta.json`,
      ];
      for (const url of candidates) {
        try {
          const resp = await fetch(url, { mode: 'cors', __feebdack_internal: true } as any);
          if (!resp.ok) continue;
          const contentType = resp.headers.get('content-type') || '';
          if (!contentType.includes('json')) continue;
          const data = await resp.json();
          const ver = data.version || data.build_version || data.buildVersion || data.app_version;
          if (ver && typeof ver === 'string' && !cancelled) {
            iframeContextRef.current.build_version = ver;
            break;
          }
        } catch {}
      }
    };
    discover();
    return () => { cancelled = true; };
  }, [currentIframeUrl]);

  // Listen for context messages sent from the iframe application
  // The client app should call: window.parent.postMessage({ type: 'feebdack_context', build_version: '...', url: '...', route: '...' }, '*')
  // The SDK should also respond to { type: 'feebdack_request_context' } with a feebdack_context message.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'feebdack_context') return;
      // Accept build_version or fallback field names the SDK might use
      const buildVer = data.build_version ?? data.version ?? data.app_version ?? data.appVersion;
      if (buildVer != null && buildVer !== '') iframeContextRef.current.build_version = String(buildVer);
      if (data.url)   iframeContextRef.current.url   = String(data.url);
      if (data.route) iframeContextRef.current.route = String(data.route);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Combined feedback items with drag and drop ordering
  const allFeedbackItems = useMemo(() => {
    console.log("🔄 Computing allFeedbackItems:", {
      feedbackItemsOrderLength: feedbackItemsOrder.length,
      screenshotsCount: screenshots.length,
      recordingsCount: recordings.length,
      orderArray: feedbackItemsOrder,
    });

    // If we have a defined order array, use it to build the items array
    if (feedbackItemsOrder.length > 0) {
      // Create lookup maps for O(1) access
      const screenshotMap = new Map(
        screenshots.map((item) => [`screenshot-${item.id}`, item]),
      );
      const recordingMap = new Map(
        recordings.map((item) => [`recording-${item.id}`, item]),
      );

      // Build items array by following the order array
      const orderedItems: FeedbackItem[] = [];

      feedbackItemsOrder.forEach((itemKey, index) => {
        if (itemKey.startsWith("screenshot-")) {
          const screenshot = screenshotMap.get(itemKey);
          if (screenshot) {
            orderedItems.push({
              id: screenshot.id,
              type: "screenshot" as const,
              timestamp: screenshot.timestamp,
              order: index,
              annotatedImage: screenshot.annotatedImage,
              originalImage: screenshot.originalImage,
              feedback: screenshot.feedback,
              hasAudio: screenshot.hasAudio || false,
              audioUrl: screenshot.audioUrl,
              audioFilename: screenshot.audioFilename,
              question: screenshot.question,
              user: screenshot.user,
              isSelected: screenshot.isSelected || false,
              uniqueFeedbackId: screenshot.uniqueFeedbackId || screenshot.id,
              apiFeedbackId: screenshot.apiFeedbackId,
            });
          }
        } else if (itemKey.startsWith("recording-")) {
          const recording = recordingMap.get(itemKey);
          if (recording) {
            orderedItems.push({
              id: recording.id,
              type: "recording" as const,
              timestamp: recording.timestamp,
              order: index,
              dataUrl: recording.dataUrl,
              recordingBlob: recording.recordingBlob,
              duration: recording.duration,
              feedback: recording.feedback,
              hasAudio: false,
              audioUrl: undefined,
              audioFilename: undefined,
              question: recording.question,
              user: recording.user,
              isSelected: recording.isSelected || false,
              uniqueFeedbackId: recording.uniqueFeedbackId || recording.id,
              apiFeedbackId: recording.apiFeedbackId,
              thumbnailUrl: recording.thumbnailUrl,
            });
          }
        }
      });

      console.log("✅ allFeedbackItems built from order array:", {
        totalItems: orderedItems.length,
        sequence: orderedItems.map((item, idx) => ({
          position: idx + 1,
          type: item.type,
          id: item.id,
        })),
      });

      return orderedItems;
    }

    // Fallback: No order array yet, sort by timestamp
    // This happens when items are first added (before being saved)
    const combined: FeedbackItem[] = [
      ...screenshots.map((item) => ({
        ...item,
        type: "screenshot" as const,
        order: -1,
        hasAudio: item.hasAudio || false,
        audioUrl: item.audioUrl,
        audioFilename: item.audioFilename,
        uniqueFeedbackId: item.uniqueFeedbackId || item.id,
        apiFeedbackId: item.apiFeedbackId,
      })),
      ...recordings.map((item) => ({
        ...item,
        type: "recording" as const,
        order: -1,
        hasAudio: false,
        audioUrl: undefined,
        audioFilename: undefined,
        recordingBlob: item.recordingBlob,
        uniqueFeedbackId: item.uniqueFeedbackId || item.id,
        apiFeedbackId: item.apiFeedbackId,
        thumbnailUrl: item.thumbnailUrl,
      })),
    ];

    const sorted = combined.sort((a, b) => a.timestamp - b.timestamp);

    console.log("✅ allFeedbackItems sorted by timestamp (fallback):", {
      totalItems: sorted.length,
      sequence: sorted.map((item, idx) => ({
        position: idx + 1,
        type: item.type,
        id: item.id,
      })),
    });

    return sorted;
  }, [screenshots, recordings, feedbackItemsOrder]);

  const totalFeedbackItems = allFeedbackItems.length;

  useEffect(() => {
    const currentCount = allFeedbackItems.length + loadingItems.length;

    // Only scroll if count increased (new item added)
    if (
      currentCount > prevFeedbackCountRef.current &&
      sidebarItemsRef.current
    ) {
      // Smooth scroll to bottom
      sidebarItemsRef.current.scrollTo({
        top: sidebarItemsRef.current.scrollHeight,
        behavior: "smooth",
      });

      console.log("📜 Auto-scrolling sidebar to show new feedback item", {
        previousCount: prevFeedbackCountRef.current,
        currentCount,
        scrollHeight: sidebarItemsRef.current.scrollHeight,
      });
    }

    // Update the previous count
    prevFeedbackCountRef.current = currentCount;
  }, [allFeedbackItems.length, loadingItems.length]);

  // ✅ NEW: Listen for upload completions and refresh sidebar
  useEffect(() => {
    const handleUploadCompleted = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId: uploadCardId, feedbackId } = event.detail;

      // Only refresh if it's for the current card
      if (uploadCardId === cardId) {
        console.log(
          "⚡ Upload completed in FeedbackWorkspace, refreshing sidebar:",
          {
            uploadCardId,
            feedbackId,
            currentCardId: cardId,
          },
        );

        // Trigger a refresh of the feedback data
        refreshFeedbackData();
      }
    };

    const handleCardRefresh = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId: refreshCardId, immediate } = event.detail;

      if (immediate && refreshCardId === cardId) {
        console.log("⚡ IMMEDIATE card refresh triggered in FeedbackWorkspace");
        refreshFeedbackData();
      }
    };

    window.addEventListener(
      "upload-item-completed",
      handleUploadCompleted as EventListener,
    );
    window.addEventListener(
      "refresh-card-data",
      handleCardRefresh as EventListener,
    );

    return () => {
      window.removeEventListener(
        "upload-item-completed",
        handleUploadCompleted as EventListener,
      );
      window.removeEventListener(
        "refresh-card-data",
        handleCardRefresh as EventListener,
      );
    };
  }, [cardId]);

  // ✅ NEW: Function to refresh feedback data from API
  const refreshFeedbackData = async () => {
    if (!cardId) {
      console.warn("⚠️ No cardId available for refresh");
      return;
    }

    console.log(
      "📡 Fetching fresh feedback data for FeedbackWorkspace:",
      cardId,
    );

    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        console.error("❌ No access token available for refresh");
        return;
      }

      const response = await fetch(
        API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        console.error("❌ Failed to fetch feedback data:", response.status);
        return;
      }

      const data = await response.json();
      console.log(
        "✅ Fresh feedback data received in FeedbackWorkspace:",
        data,
      );

      // Transform API response to FeedbackItem format
      if (data.feedbacks && Array.isArray(data.feedbacks)) {
        const transformedItems: FeedbackItem[] = await Promise.all(
          data.feedbacks
            .filter(
              (fb: any) =>
                fb.feedback_message ||
                fb.feedback_screenshot ||
                fb.feedback_recording ||
                fb.feedback_voice,
            )
            .map(async (fb: any, index: number) => {
              const feedbackId = fb.feedback_id.toString();

              // Determine feedback type
              let itemType: "screenshot" | "recording" = "screenshot";
              if (fb.feedback_recording && !fb.feedback_screenshot) {
                itemType = "recording";
              } else if (fb.feedback_recording && fb.feedback_screenshot) {
                itemType = "recording";
              }

              const feedbackItem: FeedbackItem = {
                id: feedbackId,
                type: itemType,
                timestamp: new Date(fb.created_at).getTime(),
                order: index + 1,
                feedback: fb.feedback_message || "",
                hasAudio: !!fb.feedback_voice,
                audioUrl: fb.feedback_voice
                  ? `${BASE_URL}${fb.feedback_voice}`
                  : undefined,
                audioFilename: fb.feedback_voice
                  ? `audio_${fb.feedback_id}.mp3`
                  : undefined,
                user: {
                  name: "User",
                  avatar: AvatarIcon,
                },
                isSelected: false,
                apiFeedbackId: fb.feedback_id,
                uniqueFeedbackId: feedbackId,
              };

              // Handle screenshot
              if (itemType === "screenshot" && fb.feedback_screenshot) {
                const screenshotUrl = `${BASE_URL}${fb.feedback_screenshot}`;
                feedbackItem.annotatedImage = screenshotUrl;
                feedbackItem.originalImage = screenshotUrl;
              }

              // Handle recording
              if (itemType === "recording" && fb.feedback_recording) {
                const recordingUrl = `${BASE_URL}${fb.feedback_recording}`;
                feedbackItem.dataUrl = recordingUrl;
                feedbackItem.duration = 30;

                // Load recording blob
                try {
                  const blobResponse = await fetch(recordingUrl);
                  const blob = await blobResponse.blob();
                  feedbackItem.recordingBlob = blob;
                } catch (error) {
                  console.warn(
                    `⚠️ Failed to load recording blob for ${feedbackId}:`,
                    error,
                  );
                  feedbackItem.recordingBlob = new Blob([], {
                    type: "video/mp4",
                  });
                }

                // Load thumbnail — use server URL if available, otherwise generate from blob
                if (fb.feedback_thumbnail) {
                  feedbackItem.thumbnailUrl = `${BASE_URL}${fb.feedback_thumbnail}`;
                } else if (feedbackItem.recordingBlob && feedbackItem.recordingBlob.size > 0) {
                  try {
                    const blobUrl = URL.createObjectURL(feedbackItem.recordingBlob);
                    feedbackItem.thumbnailUrl = await generateVideoThumbnail(blobUrl);
                    URL.revokeObjectURL(blobUrl);
                  } catch (e) {
                    console.warn(`⚠️ Could not generate thumbnail for recording ${feedbackId}:`, e);
                  }
                }
              }

              return feedbackItem;
            }),
        );

        // ✅ Sort by timestamp (oldest first, newest last) - NEW items appear at bottom
        const sortedItems = transformedItems.sort(
          (a, b) => a.timestamp - b.timestamp,
        );

        console.log("✅ Refreshed feedback items in FeedbackWorkspace:", {
          totalItems: sortedItems.length,
          newItems: sortedItems.filter(
            (item) =>
              !screenshots.some((s) => s.id === item.id) &&
              !recordings.some((r) => r.id === item.id),
          ).length,
        });

        // ✅ Update screenshots and recordings state
        const newScreenshots: ScreenshotSubmissionEnhanced[] = sortedItems
          .filter((item) => item.type === "screenshot" && item.annotatedImage)
          .map((item) => ({
            id: item.id,
            annotatedImage: item.annotatedImage!,
            originalImage: item.originalImage || item.annotatedImage!,
            feedback: item.feedback || "",
            timestamp: item.timestamp,
            hasAudio: item.hasAudio || false,
            audioUrl: item.audioUrl,
            audioFilename: item.audioFilename,
            uniqueFeedbackId: item.uniqueFeedbackId,
            apiFeedbackId: item.apiFeedbackId,
          }));

        const newRecordings: RecordingSubmissionEnhanced[] = sortedItems
          .filter((item) => item.type === "recording" && item.dataUrl)
          .map((item) => ({
            id: item.id,
            recordingBlob:
              item.recordingBlob || new Blob([], { type: "video/mp4" }),
            dataUrl: item.dataUrl!,
            feedback: item.feedback || "",
            timestamp: item.timestamp,
            duration: item.duration || 30,
            hasAudio: false,
            uniqueFeedbackId: item.uniqueFeedbackId,
            apiFeedbackId: item.apiFeedbackId,
            thumbnailUrl: item.thumbnailUrl,
          }));

        // ✅ Update state with refreshed data
        setScreenshots(newScreenshots);
        setRecordings(newRecordings);

        // ✅ Update order array — preserve existing order, only append new items.
        // Replacing the whole order array with a fresh API-derived list causes the
        // sidebar to jump to newest-first whenever an upload completes.
        const newOrder = sortedItems.map((item) => `${item.type}-${item.id}`);
        setFeedbackItemsOrder((prevOrder) => {
          // If there's no valid existing order yet, use the ascending API order.
          const hasValidOrder =
            prevOrder.length > 0 && prevOrder[0]?.includes("-");
          if (!hasValidOrder) return newOrder;

          // Keep existing items in their current position; append only items that
          // aren't already tracked (e.g. added by a collaborator or another device).
          const existingIds = new Set(prevOrder);
          const brandNewItems = newOrder.filter((id) => !existingIds.has(id));
          return brandNewItems.length > 0
            ? [...prevOrder, ...brandNewItems]
            : prevOrder;
        });

        console.log("✅ FeedbackWorkspace sidebar refreshed with new items");
      }
    } catch (error) {
      console.error(
        "❌ Error refreshing feedback data in FeedbackWorkspace:",
        error,
      );
    }
  };

  useEffect(() => {
    if (totalFeedbackItems > 0 && !isUrlLocked) {
      setIsUrlLocked(true);
      console.log("🔒 URL locked after first feedback item added");
    }
  }, [totalFeedbackItems, isUrlLocked]);

  // Update order when new items are added
  useEffect(() => {
    const currentIds = new Set(feedbackItemsOrder);
    const newItems: string[] = [];

    screenshots.forEach((item) => {
      const id = `screenshot-${item.id}`;
      if (!currentIds.has(id)) {
        newItems.push(id);
      }
    });

    recordings.forEach((item) => {
      const id = `recording-${item.id}`;
      if (!currentIds.has(id)) {
        newItems.push(id);
      }
    });

    if (newItems.length > 0) {
      setFeedbackItemsOrder((prev) => [...prev, ...newItems]);
    }
  }, [screenshots, recordings, feedbackItemsOrder]);

  // Clean up order when items are removed
  useEffect(() => {
    const validIds = new Set([
      ...screenshots.map((item) => `screenshot-${item.id}`),
      ...recordings.map((item) => `recording-${item.id}`),
    ]);

    setFeedbackItemsOrder((prev) => prev.filter((id) => validIds.has(id)));
  }, [screenshots, recordings]);

  // Lazily generate thumbnails for any recording that doesn't have one yet.
  // Uses a ref-based attempt set so each recording is only tried once per session,
  // and updating thumbnailUrl won't re-trigger this effect infinitely.
  useEffect(() => {
    const missing = recordings.filter(
      (r) => !r.thumbnailUrl && !thumbnailAttemptsRef.current.has(r.id),
    );
    if (missing.length === 0) return;

    missing.forEach(async (recording) => {
      thumbnailAttemptsRef.current.add(recording.id);

      const hasBlob = recording.recordingBlob && recording.recordingBlob.size > 0;
      const hasServerUrl = recording.dataUrl?.startsWith("http");
      if (!hasBlob && !hasServerUrl) return;

      let blobUrl: string | null = null;
      try {
        if (hasBlob) {
          blobUrl = URL.createObjectURL(recording.recordingBlob!);
        }
        const thumbnailUrl = await generateVideoThumbnail(blobUrl || recording.dataUrl!);
        setRecordings((prev) =>
          prev.map((r) =>
            r.id === recording.id && !r.thumbnailUrl ? { ...r, thumbnailUrl } : r,
          ),
        );
      } catch (e) {
        console.warn(`⚠️ Could not generate thumbnail for recording ${recording.id}:`, e);
      } finally {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordings.map((r) => r.id).join(",")]);

  // ✅ NEW: Load and filter recent URLs when input changes
  useEffect(() => {
    const recentUrls = getRecentUrls();
    const filtered = filterUrls(recentUrls, inputUrl);
    setFilteredUrls(filtered);
  }, [inputUrl]);

  // Modified URL submit handler - creates initial feedback for the URL
  const handleUrlSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Close dropdown
      setShowUrlDropdown(false);
      setSelectedSuggestionIndex(-1);

      // If URL is locked, only allow reload
      if (isUrlLocked) {
        if (currentIframeUrl) {
          setIframeKey((prev) => prev + 1);
          console.log("🔄 Reloading locked URL:", currentIframeUrl);
        }
        return;
      }

      const formattedUrl = formatUrl(inputUrl);

      if (!formattedUrl) {
        alert("Please enter a URL.");
        return;
      }

      if (!isValidHttpUrl(formattedUrl)) {
        alert("Please enter a valid URL.");
        return;
      }

      // ✅ NEW: Save to recent URLs
      saveRecentUrl(formattedUrl);

      setOriginalFeedbackName(feedbackName || `Feedback for ${formattedUrl}`);

      const newHistory = [
        ...iframeHistory.slice(0, iframeHistoryIndex + 1),
        formattedUrl,
      ];

      setIframeHistory(newHistory);
      setIframeHistoryIndex(newHistory.length - 1);
      setUrl(formattedUrl);
      setShowIframe(true);
    },
    [
      inputUrl,
      iframeHistory,
      iframeHistoryIndex,
      feedbackName,
      isUrlLocked,
      currentIframeUrl,
    ],
  );

  // ✅ FIXED: Handle URL suggestion click with proper state synchronization
  const handleSuggestionClick = useCallback(
    async (selectedUrl: string) => {
      // Close dropdown immediately
      setShowUrlDropdown(false);
      setSelectedSuggestionIndex(-1);

      // Update inputUrl state
      setInputUrl(selectedUrl);

      // Now proceed with URL submission using the selected URL
      if (isUrlLocked) {
        if (currentIframeUrl) {
          setIframeKey((prev) => prev + 1);
          console.log("🔄 Reloading locked URL:", currentIframeUrl);
        }
        return;
      }

      const formattedUrl = formatUrl(selectedUrl);

      if (!formattedUrl) {
        alert("Please enter a URL.");
        return;
      }

      if (!isValidHttpUrl(formattedUrl)) {
        alert("Please enter a valid URL.");
        return;
      }

      // ✅ Save to recent URLs
      saveRecentUrl(formattedUrl);

      setOriginalFeedbackName(feedbackName || `Feedback for ${formattedUrl}`);

      const newHistory = [
        ...iframeHistory.slice(0, iframeHistoryIndex + 1),
        formattedUrl,
      ];

      setFeedbackId(undefined);
      setIframeHistory(newHistory);
      setIframeHistoryIndex(newHistory.length - 1);
      setUrl(formattedUrl);
      setShowIframe(true);

      await new Promise((resolve) => setTimeout(resolve, 100));
    },
    [
      isUrlLocked,
      currentIframeUrl,
      feedbackName,
      iframeHistory,
      iframeHistoryIndex,
    ],
  );

  // ✅ NEW: Handle keyboard navigation in dropdown
  const handleUrlInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showUrlDropdown || filteredUrls.length === 0) {
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < filteredUrls.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          if (selectedSuggestionIndex >= 0) {
            e.preventDefault();
            handleSuggestionClick(filteredUrls[selectedSuggestionIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowUrlDropdown(false);
          setSelectedSuggestionIndex(-1);
          break;
      }
    },
    [
      showUrlDropdown,
      filteredUrls,
      selectedSuggestionIndex,
      handleSuggestionClick,
    ],
  );

  // ✅ NEW: Handle input focus
  const handleUrlInputFocus = useCallback(() => {
    if (!isUrlLocked) {
      const recentUrls = getRecentUrls();
      if (recentUrls.length > 0) {
        setShowUrlDropdown(true);
        // If input is empty, show all recent URLs
        if (inputUrl.trim() === "") {
          setFilteredUrls(recentUrls.slice(0, 5));
        }
      }
    }
  }, [inputUrl, isUrlLocked]);

  const uploadWithRetry = async <T,>(
    uploadFn: () => Promise<T>,
    maxRetries: number = 3,
    delayMs: number = 1000,
  ): Promise<T> => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📤 Upload attempt ${attempt}/${maxRetries}`);
        const result = await uploadFn();
        console.log(`✅ Upload successful on attempt ${attempt}`);
        return result;
      } catch (error) {
        lastError = error as Error;
        console.warn(`⚠️ Upload attempt ${attempt} failed:`, error);

        // Don't retry on authentication errors
        const errorMsg = (error as Error).message.toLowerCase();
        if (errorMsg.includes("401") || errorMsg.includes("authentication")) {
          throw error;
        }

        // Wait before retry (with exponential backoff)
        if (attempt < maxRetries) {
          const waitTime = delayMs * Math.pow(2, attempt - 1);
          console.log(`⏳ Waiting ${waitTime}ms before retry...`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      }
    }

    throw lastError || new Error("Upload failed after all retries");
  };

  const showFsToast = useCallback((message: string, type: "success" | "error" = "success") => {
    if (fsToastTimerRef.current) clearTimeout(fsToastTimerRef.current);
    setFsToast({ message, visible: true, type });
    fsToastTimerRef.current = setTimeout(() => setFsToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  const processScreenshotUpload = useCallback(
    async (
      submission: ScreenshotSubmission,
      skipLoadingState: boolean = false,
    ) => {
      console.log("📤 Processing screenshot upload:", {
        id: submission.id,
        hasAudio: submission.hasAudio,
        skipLoadingState,
      });

      // Capture NOW (before any await) so viewport/url/route reflect the moment the screenshot was taken
      const envContext = getTechnicalContext(iframeContextRef.current);
      const envConsoleErrors = getConsoleErrors();
      const envFailedRequests = getFailedRequests();

      // ✅ ADD TO UPLOAD QUEUE IMMEDIATELY
      UploadQueueManager.addToQueue({
        id: submission.id,
        cardId: cardId,
        type: "screenshot",
        feedbackId: "", // Will update after creation
        timestamp: Date.now(),
      });

      if (!skipLoadingState) {
        const loadingItem = {
          id: submission.id,
          type: "screenshot" as const,
          timestamp: Date.now(),
          progress: 0,
        };
        setLoadingItems((prev) => [...prev, loadingItem]);
      }

      // Compress image if needed BEFORE creating feedback
      let compressedAnnotatedImage = submission.annotatedImage;
      if (
        submission.annotatedImage &&
        needsCompression(submission.annotatedImage, 0.8)
      ) {
        console.log("🗜️ Compressing screenshot before upload...");
        try {
          compressedAnnotatedImage = await compressImage(
            submission.annotatedImage,
            {
              maxSizeMB: 0.8,
              initialQuality: 0.85,
              maxDimension: 1920,
              format: "auto",
              minQuality: 0.3,
            },
          );
        } catch (compressionError) {
          console.warn(
            "⚠️ Compression failed, using original:",
            compressionError,
          );
        }
      }

      // STEP 1: Create NEW feedback ID
      let newFeedbackId: string | undefined;

      if (url && isValidHttpUrl(url)) {
        try {
          setIsCreatingFeedback(true);

          const feedbackResponse = await ApiService.createFeedbackForItem(
            cardId,
            workspaceId,
            url,
            "screenshot",
            submission.id,
            originalFeedbackName || feedbackName,
          );

          if (feedbackResponse && feedbackResponse.id) {
            newFeedbackId = feedbackResponse.id;
            console.log("✅ Created NEW feedback ID:", newFeedbackId);

            // ✅ UPDATE QUEUE WITH FEEDBACK ID
            UploadQueueManager.updateItem(submission.id, {
              feedbackId: newFeedbackId,
              status: "uploading",
            });

            // Fire-and-forget: send technical metadata for this screenshot.
            // keepalive:true ensures the request completes even if the user navigates away.
            ApiService.uploadEnvironmentData(
              newFeedbackId,
              envContext,
              envConsoleErrors,
              envFailedRequests,
            ).catch(() => {});
          } else {
            throw new Error("Failed to create feedback ID");
          }
        } catch (error) {
          console.error("🚨 Failed to create feedback ID:", error);

          // ✅ UPDATE QUEUE WITH ERROR
          UploadQueueManager.updateItem(submission.id, {
            status: "failed",
            error: error instanceof Error ? error.message : "Unknown error",
          });

          showFsToast("Failed to save screenshot. Please try again.", "error");
          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id),
          );
          return;
        } finally {
          setIsCreatingFeedback(false);
        }
      }

      // ✅ STEP 2: PARALLEL UPLOADS with progress tracking
      if (newFeedbackId) {
        console.log("🚀 Starting PARALLEL uploads for screenshot...");
        const uploadStartTime = Date.now();

        const uploadTasks: { name: string; promise: Promise<any> }[] = [];

        // Message upload
        if (submission.feedback && submission.feedback.trim().length > 0) {
          uploadTasks.push({
            name: "message",
            promise: uploadWithRetry(
              async () => {
                console.log("💬 [PARALLEL] Starting message upload...");
                await ApiService.uploadMessage(
                  submission.feedback,
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS
                UploadQueueManager.updateItem(submission.id, {
                  progress: 33,
                });

                console.log("✅ [PARALLEL] Message upload complete");
                return { type: "message", success: true };
              },
              3,
              1000,
            ).catch((error) => {
              console.error("❌ [PARALLEL] Message upload failed:", error);
              return { type: "message", success: false, error };
            }),
          });
        }

        // Screenshot image upload
        if (compressedAnnotatedImage) {
          uploadTasks.push({
            name: "screenshot",
            promise: uploadWithRetry(
              async () => {
                console.log("🖼️ [PARALLEL] Starting screenshot upload...");

                const response = await fetch(compressedAnnotatedImage);
                const blob = await response.blob();
                const pngBlob = new Blob([blob], { type: "image/png" });

                await ApiService.uploadFile(
                  pngBlob,
                  `screenshot_${submission.id}_${Date.now()}.png`,
                  "screenshot",
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS
                UploadQueueManager.updateItem(submission.id, {
                  progress: 66,
                });

                console.log("✅ [PARALLEL] Screenshot upload complete");
                return { type: "screenshot", success: true };
              },
              3,
              2000,
            ).catch((error) => {
              console.error("❌ [PARALLEL] Screenshot upload failed:", error);
              return { type: "screenshot", success: false, error };
            }),
          });
        }

        // Audio upload
        if (submission.hasAudio && submission.audioBlob) {
          uploadTasks.push({
            name: "audio",
            promise: uploadWithRetry(
              async () => {
                console.log("🎵 [PARALLEL] Starting audio upload...");
                await ApiService.uploadFile(
                  submission.audioBlob!,
                  submission.audioFilename || `audio_${submission.id}.mp3`,
                  "audio",
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS
                UploadQueueManager.updateItem(submission.id, {
                  progress: 100,
                });

                console.log("✅ [PARALLEL] Audio upload complete");
                return { type: "audio", success: true };
              },
              3,
              1000,
            ).catch((error) => {
              console.error("❌ [PARALLEL] Audio upload failed:", error);
              return { type: "audio", success: false, error };
            }),
          });
        }

        // Wait for all uploads
        const results = await Promise.all(uploadTasks.map((t) => t.promise));

        const failedUploads = results.filter((r) => !r.success);
        const screenshotResult = results.find((r) => r.type === "screenshot");

        if (screenshotResult && !screenshotResult.success) {
          console.error("🚨 Critical upload failed:", screenshotResult.error);

          // ✅ UPDATE QUEUE WITH FAILURE
          UploadQueueManager.updateItem(submission.id, {
            status: "failed",
            error: screenshotResult.error?.message || "Upload failed",
          });

          showFsToast(
            `Screenshot upload failed: ${screenshotResult.error?.message || "Unknown error"}`,
            "error",
          );
          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id),
          );
          return;
        }

        // ✅ MARK AS COMPLETED IN QUEUE
        UploadQueueManager.updateItem(submission.id, {
          status: "completed",
          progress: 100,
        });

        console.log("✅ All uploads completed successfully");
      }

      // Update progress bar
      setLoadingItems((prev) =>
        prev.map((item) =>
          item.id === submission.id ? { ...item, progress: 100 } : item,
        ),
      );

      await new Promise((resolve) => setTimeout(resolve, 300));

      const enhancedSubmission: ScreenshotSubmissionEnhanced = {
        ...submission,
        uniqueFeedbackId: newFeedbackId,
        apiFeedbackId: newFeedbackId ? parseInt(newFeedbackId) : undefined,
      };

      setLoadingItems((prev) =>
        prev.filter((item) => item.id !== submission.id),
      );

      requestAnimationFrame(() => {
        setScreenshots((prev) => [...prev, enhancedSubmission]);
      });

      if (
        isFirstScreenshot &&
        submission.annotatedImage &&
        onFirstScreenshotTaken
      ) {
        onFirstScreenshotTaken(submission.annotatedImage);
        setIsFirstScreenshot(false);
      }

      if (!hasAddedFeedback) {
        setHasAddedFeedback(true);
      }

      if (newFeedbackId) {
        setFeedbackId(newFeedbackId);
      }

      // ✅ CLEAN UP COMPLETED ITEM AFTER SHORT DELAY
      setTimeout(() => {
        UploadQueueManager.removeItem(submission.id);
      }, 5000);
    },
    [
      url,
      cardId,
      workspaceId,
      originalFeedbackName,
      feedbackName,
      isFirstScreenshot,
      onFirstScreenshotTaken,
      hasAddedFeedback,
      showFsToast,
    ],
  );

  const handleScreenshotSubmit = useCallback(
    async (submission: ScreenshotSubmission) => {
      console.log("📸 FeedbackWorkspace - Screenshot submission received:", {
        id: submission.id,
        hasAudio: submission.hasAudio,
        uniqueFeedbackId: submission.uniqueFeedbackId,
        isFirstScreenshot: isFirstScreenshot,
        feedbackLength: submission.feedback?.length,
      });
      processScreenshotUpload(submission);
      if (isWorkspaceFullscreen) showFsToast("Screenshot saved to feedback");
    },
    [isFirstScreenshot, processScreenshotUpload, isWorkspaceFullscreen, showFsToast],
  );

  // ADD this NEW function to handle recording uploads:

  const processRecordingUpload = useCallback(
    async (
      submission: RecordingSubmission,
      skipLoadingState: boolean = false,
    ) => {
      console.log("📤 Processing recording upload:", {
        id: submission.id,
        duration: submission.duration,
        skipLoadingState,
        hasThumbnail: !!submission.thumbnailUrl,
        hasAnnotation: !!submission.annotationImage,
        hasRecordingBlob: !!submission.recordingBlob,
        blobSize: submission.recordingBlob?.size || 0,
      });

      // Validate recordingBlob exists before proceeding
      if (!submission.recordingBlob || submission.recordingBlob.size === 0) {
        console.error("❌ Cannot process recording: Invalid or missing blob");
        alert(
          "Recording data is not ready. Please wait a moment and try again.",
        );
        return;
      }

      // Capture NOW (before any await) so viewport/url/route reflect the moment the recording was submitted
      const envContext = getTechnicalContext(iframeContextRef.current);
      const envConsoleErrors = getConsoleErrors();
      const envFailedRequests = getFailedRequests();

      // ✅ ADD TO UPLOAD QUEUE IMMEDIATELY
      UploadQueueManager.addToQueue({
        id: submission.id,
        cardId: cardId,
        type: "recording",
        feedbackId: "", // Will update after creation
        timestamp: Date.now(),
        hasThumbnail: !!submission.thumbnailUrl,
        hasAnnotation: !!submission.annotationImage,
      });

      // Add to loading state
      if (!skipLoadingState) {
        const loadingItem = {
          id: submission.id,
          type: "recording" as const,
          timestamp: Date.now(),
          progress: 0,
        };
        setLoadingItems((prev) => [...prev, loadingItem]);
      }

      // Compress video if needed
      let processedBlob: Blob = submission.recordingBlob;
      const originalSize = submission.recordingBlob.size;
      const originalSizeMB = originalSize / (1024 * 1024);

      if (needsVideoCompression(submission.recordingBlob, 1)) {
        try {
          console.log("🗜️ Starting video compression...");
          console.log(`📦 Original size: ${originalSizeMB.toFixed(2)} MB`);

          setLoadingItems((prev) =>
            prev.map((item) =>
              item.id === submission.id ? { ...item, progress: 10 } : item,
            ),
          );

          UploadQueueManager.updateItem(submission.id, {
            progress: 10,
          });

          const compressionResult = await compressVideo(
            submission.recordingBlob,
            {
              targetSizePercent: 0.35,
              maxWidth: 1280,
              maxHeight: 720,
              frameRate: 24,
            },
          );

          processedBlob = compressionResult.blob;
          const compressedSizeMB =
            compressionResult.compressedSize / (1024 * 1024);

          console.log("✅ Video compression complete:");
          console.log(`📦 Compressed: ${compressedSizeMB.toFixed(2)} MB`);

          setLoadingItems((prev) =>
            prev.map((item) =>
              item.id === submission.id ? { ...item, progress: 25 } : item,
            ),
          );

          UploadQueueManager.updateItem(submission.id, {
            progress: 25,
          });
        } catch (compressionError) {
          console.warn(
            "⚠️ Video compression failed, using original:",
            compressionError,
          );
          processedBlob = submission.recordingBlob;
        }
      }

      // Create NEW feedback ID
      let newFeedbackId: string | undefined;

      if (url && isValidHttpUrl(url)) {
        try {
          setIsCreatingFeedback(true);

          const feedbackResponse = await ApiService.createFeedbackForItem(
            cardId,
            workspaceId,
            url,
            "recording",
            submission.id,
            originalFeedbackName || feedbackName,
          );

          if (feedbackResponse && feedbackResponse.id) {
            newFeedbackId = feedbackResponse.id;
            console.log(
              "✅ Created NEW feedback ID for recording:",
              newFeedbackId,
            );

            // ✅ UPDATE QUEUE WITH FEEDBACK ID
            UploadQueueManager.updateItem(submission.id, {
              feedbackId: newFeedbackId,
              status: "uploading",
            });

            // Fire-and-forget: send technical metadata for this recording.
            // keepalive:true ensures the request completes even if the user navigates away.
            ApiService.uploadEnvironmentData(
              newFeedbackId,
              envContext,
              envConsoleErrors,
              envFailedRequests,
            ).catch(() => {});
          } else {
            throw new Error("Failed to create feedback ID for recording");
          }
        } catch (error) {
          console.error(
            "🚨 Failed to create new feedback ID for recording:",
            error,
          );

          // ✅ UPDATE QUEUE WITH ERROR
          UploadQueueManager.updateItem(submission.id, {
            status: "failed",
            error:
              error instanceof Error
                ? error.message
                : "Failed to create feedback",
          });

          showFsToast("Failed to save recording. Please try again.", "error");
          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id),
          );
          return;
        } finally {
          setIsCreatingFeedback(false);
        }
      }

      // PARALLEL UPLOADS with progress tracking
      if (newFeedbackId) {
        console.log("🚀 Starting PARALLEL uploads for recording...");
        const uploadStartTime = Date.now();

        const uploadTasks: { name: string; promise: Promise<any> }[] = [];

        // 1. Message upload (small, fast)
        if (submission.feedback && submission.feedback.trim().length > 0) {
          uploadTasks.push({
            name: "message",
            promise: uploadWithRetry(
              async () => {
                console.log("💬 [PARALLEL] Starting message upload...");
                await ApiService.uploadMessage(
                  submission.feedback,
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS
                UploadQueueManager.updateItem(submission.id, {
                  progress: 35,
                });

                console.log("✅ [PARALLEL] Message upload complete");
                return { type: "message", success: true };
              },
              3,
              1000,
            ).catch((error) => {
              console.error("❌ [PARALLEL] Message upload failed:", error);
              return { type: "message", success: false, error };
            }),
          });
        }

        // 2. Thumbnail upload (small, fast)
        if (submission.thumbnailUrl) {
          uploadTasks.push({
            name: "thumbnail",
            promise: (async () => {
              try {
                console.log("🖼️ [PARALLEL] Starting thumbnail upload...");
                const thumbnailResult = await ApiService.uploadVideoThumbnail(
                  submission.thumbnailUrl!,
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS
                UploadQueueManager.updateItem(submission.id, {
                  progress: 45,
                });

                console.log("✅ [PARALLEL] Thumbnail upload complete");
                return {
                  type: "thumbnail",
                  success: true,
                  result: thumbnailResult,
                };
              } catch (error) {
                console.warn("⚠️ [PARALLEL] Thumbnail upload failed:", error);
                return { type: "thumbnail", success: false, error };
              }
            })(),
          });
        }

        // 3. Annotation image upload (small, fast)
        if (submission.annotationImage) {
          uploadTasks.push({
            name: "annotation",
            promise: (async () => {
              try {
                console.log("🎨 [PARALLEL] Starting annotation upload...");
                const response = await fetch(submission.annotationImage!);
                const annotationBlob = await response.blob();
                const pngBlob = new Blob([annotationBlob], {
                  type: "image/png",
                });

                const annotationResult = await ApiService.uploadFile(
                  pngBlob,
                  `recording_${submission.id}_annotations.png`,
                  "screenshot",
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS
                UploadQueueManager.updateItem(submission.id, {
                  progress: 55,
                });

                console.log("✅ [PARALLEL] Annotation upload complete");
                return {
                  type: "annotation",
                  success: true,
                  result: annotationResult,
                };
              } catch (error) {
                console.warn("⚠️ [PARALLEL] Annotation upload failed:", error);
                return { type: "annotation", success: false, error };
              }
            })(),
          });
        }

        // 4. Video upload (large, slow - but runs in parallel with others)
        if (processedBlob && processedBlob.size > 0) {
          uploadTasks.push({
            name: "video",
            promise: uploadWithRetry(
              async () => {
                console.log("📹 [PARALLEL] Starting video upload...");

                let videoBlob: Blob = processedBlob;
                let fileName = `recording_${submission.id}.mp4`;

                // Ensure proper MIME type
                if (
                  !videoBlob.type.includes("mp4") &&
                  !videoBlob.type.includes("webm")
                ) {
                  console.log("🔄 Converting blob to MP4 MIME type");
                  videoBlob = new Blob([videoBlob], { type: "video/mp4" });
                }

                if (videoBlob.size === 0) {
                  throw new Error("Video blob is empty after processing");
                }

                const result = await ApiService.uploadFile(
                  videoBlob,
                  fileName,
                  "recording",
                  newFeedbackId!,
                );

                // ✅ UPDATE PROGRESS TO 100%
                UploadQueueManager.updateItem(submission.id, {
                  progress: 100,
                });

                console.log("✅ [PARALLEL] Video upload complete");
                return { type: "video", success: true, result };
              },
              3,
              2000,
            ).catch((error) => {
              console.error("❌ [PARALLEL] Video upload failed:", error);
              return { type: "video", success: false, error };
            }),
          });
        } else {
          console.error("❌ Cannot upload recording: Blob is invalid");

          UploadQueueManager.updateItem(submission.id, {
            status: "failed",
            error: "Recording data is invalid",
          });

          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id),
          );
          showFsToast("Recording data is invalid. Please try again.", "error");
          return;
        }

        // Update progress while uploads are in progress
        setLoadingItems((prev) =>
          prev.map((item) =>
            item.id === submission.id ? { ...item, progress: 40 } : item,
          ),
        );

        // Wait for ALL uploads to complete in parallel
        console.log(`🚀 Waiting for ${uploadTasks.length} parallel uploads...`);
        const results = await Promise.all(uploadTasks.map((t) => t.promise));

        const uploadDuration = ((Date.now() - uploadStartTime) / 1000).toFixed(
          1,
        );
        console.log(`📊 All parallel uploads completed in ${uploadDuration}s`);

        // Check results
        const failedUploads = results.filter((r) => !r.success);
        const successfulUploads = results.filter((r) => r.success);

        console.log("📊 Upload results:", {
          total: results.length,
          successful: successfulUploads.length,
          failed: failedUploads.length,
          duration: `${uploadDuration}s`,
        });

        // Check if critical uploads failed (video is critical)
        const videoResult = results.find((r) => r.type === "video");
        if (videoResult && !videoResult.success) {
          console.error(
            "🚨 Critical upload (video) failed:",
            videoResult.error,
          );

          // ✅ UPDATE QUEUE WITH FAILURE
          UploadQueueManager.updateItem(submission.id, {
            status: "failed",
            error: videoResult.error?.message || "Recording upload failed",
          });

          showFsToast(
            `Recording upload failed: ${videoResult.error?.message || "Unknown error"}`,
            "error",
          );
          setLoadingItems((prev) =>
            prev.filter((item) => item.id !== submission.id),
          );
          return;
        }

        // Log any non-critical failures
        failedUploads.forEach((result) => {
          if (result.type !== "video") {
            console.warn(
              `⚠️ Non-critical upload failed: ${result.type}`,
              result.error,
            );
          }
        });

        // ✅ MARK AS COMPLETED IN QUEUE
        UploadQueueManager.updateItem(submission.id, {
          status: "completed",
          progress: 100,
        });

        console.log("✅ All critical uploads completed successfully");
      }

      // Complete progress bar to 100%
      setLoadingItems((prev) =>
        prev.map((item) =>
          item.id === submission.id ? { ...item, progress: 100 } : item,
        ),
      );

      await new Promise((resolve) => setTimeout(resolve, 300));

      const enhancedSubmission: RecordingSubmissionEnhanced = {
        ...submission,
        uniqueFeedbackId: newFeedbackId,
        apiFeedbackId: newFeedbackId ? parseInt(newFeedbackId) : undefined,
        thumbnailUrl: submission.thumbnailUrl,
      };

      setLoadingItems((prev) =>
        prev.filter((item) => item.id !== submission.id),
      );

      requestAnimationFrame(() => {
        setRecordings((prev) => [...prev, enhancedSubmission]);
      });

      if (!hasAddedFeedback) {
        setHasAddedFeedback(true);
        console.log("✅ First feedback item added - enabling save buttons");
      }

      if (newFeedbackId) {
        setFeedbackId(newFeedbackId);
      }

      // ✅ CLEAN UP COMPLETED ITEM AFTER SHORT DELAY
      setTimeout(() => {
        UploadQueueManager.removeItem(submission.id);
      }, 5000); // Keep for 5 seconds to show success
    },
    [
      url,
      cardId,
      workspaceId,
      originalFeedbackName,
      feedbackName,
      hasAddedFeedback,
      showFsToast,
    ],
  );
  const handleRecordingSubmit = useCallback(
    async (submission: RecordingSubmission) => {
      console.log("📹 FeedbackWorkspace - Recording submission received:", {
        id: submission.id,
        duration: submission.duration,
        uniqueFeedbackId: submission.uniqueFeedbackId,
        hasThumbnail: !!submission.thumbnailUrl,
      });

      processRecordingUpload(submission);
      if (isWorkspaceFullscreen) showFsToast("Recording saved to feedback");
    },
    [processRecordingUpload, isWorkspaceFullscreen, showFsToast],
  );

  // ✅ NEW: Handle URL input change with dropdown control
  const handleUrlInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputUrl(value);

      // Show dropdown when typing if not locked
      if (!isUrlLocked) {
        const recentUrls = getRecentUrls();
        if (recentUrls.length > 0) {
          setShowUrlDropdown(true);
          setSelectedSuggestionIndex(-1);
        }
      } else {
        setShowUrlDropdown(false);
      }
    },
    [isUrlLocked],
  );

  const handleToolClick = useCallback((toolId: string) => {
    console.log("🔧 FeedbackWorkspace - Tool clicked:", toolId);
    if (toolId === "screenshot" || toolId === "record") {
      setShowFloatingSidebar(false);
      setHoveredItemId(null);
      setIsCapturing(true);
    }
    if (toolId === "record") {
      // When recording toolbox opens it is wider (~270px). If the current toolbox
      // position would push the recording toolbox past the right viewport edge,
      // shift left just enough to keep it fully visible.
      const RECORDING_TOOLBOX_WIDTH = 270;
      setToolboxPos((prev) => {
        if (!prev) return prev; // default bottom-left position is fine
        const maxLeft = window.innerWidth - RECORDING_TOOLBOX_WIDTH - 8;
        if (prev.left > maxLeft) {
          return { left: Math.max(0, maxLeft), top: prev.top };
        }
        return prev;
      });
    }
  }, []);

  const handleCaptureEnd = useCallback(() => {
    setHideSidebar(false);
    setIsCapturing(false);
  }, []);

  const handleRemoveScreenshot = useCallback(
    (screenshotId: string) => {
      console.log("🗑️ Delete screenshot requested:", screenshotId);
      const screenshot = screenshots.find((s) => s.id === screenshotId);
      setItemToDelete({
        id: screenshotId,
        type: "screenshot",
        uniqueFeedbackId: screenshot?.uniqueFeedbackId,
      });
      setShowDeleteConfirm(true);
    },
    [screenshots],
  );

  // NEW: Handle edit screenshot
  const handleEditScreenshot = useCallback(
    async (screenshotId: string) => {
      console.log("✏️ Edit screenshot requested:", screenshotId);
      const screenshot = screenshots.find((s) => s.id === screenshotId);
      if (screenshot) {
        console.log("✏️ Screenshot data being passed to editor:", {
          id: screenshot.id,
          hasAnnotatedImage: !!screenshot.annotatedImage,
          hasOriginalImage: !!screenshot.originalImage,
          hasAudioUrl: !!screenshot.audioUrl,
          hasAudioBlob: !!screenshot.audioBlob,
          audioFilename: screenshot.audioFilename,
          uniqueFeedbackId: screenshot.uniqueFeedbackId,
          audioUrlType: screenshot.audioUrl?.startsWith("blob:")
            ? "blob"
            : screenshot.audioUrl?.startsWith("http")
              ? "remote"
              : "unknown",
        });

        let editAudioUrl = screenshot.audioUrl;
        let editAudioBlob = screenshot.audioBlob;

        // ✅ CRITICAL FIX: Handle all audio URL scenarios
        if (screenshot.hasAudio) {
          // ✅ CASE 1: Fresh recording - has blob and blob URL
          if (
            screenshot.audioBlob &&
            screenshot.audioUrl?.startsWith("blob:")
          ) {
            console.log("🎵 Case 1: Fresh recording with blob URL");
            // Revoke old blob URL and create fresh one
            URL.revokeObjectURL(screenshot.audioUrl);
            editAudioUrl = URL.createObjectURL(screenshot.audioBlob);
            console.log(
              "✅ Created fresh blob URL for fresh recording:",
              editAudioUrl.substring(0, 50),
            );
          }
          // ✅ CASE 2: Has blob but no URL (shouldn't happen, but handle it)
          else if (screenshot.audioBlob && !screenshot.audioUrl) {
            console.log("🎵 Case 2: Has blob but no URL - creating blob URL");
            editAudioUrl = URL.createObjectURL(screenshot.audioBlob);
            console.log(
              "✅ Created blob URL from existing blob:",
              editAudioUrl.substring(0, 50),
            );
          }
          // ✅ CASE 3: Remote URL (from API after save) - use directly
          else if (screenshot.audioUrl?.startsWith("http")) {
            console.log(
              "🎵 Case 3: Remote API URL - using directly:",
              screenshot.audioUrl.substring(0, 50),
            );
            editAudioUrl = screenshot.audioUrl;
            // ✅ Don't pass blob - VoiceNoteRecorder will fetch from API URL
            editAudioBlob = undefined;
          }
          // ✅ CASE 4: Has uniqueFeedbackId but stale blob URL - construct API URL
          else if (
            screenshot.audioUrl?.startsWith("blob:") &&
            screenshot.uniqueFeedbackId
          ) {
            console.log(
              "🎵 Case 4: Stale blob URL - constructing API URL from feedbackId",
            );
            editAudioUrl = `${API_URLS.GET_FILE_VOICE(
              screenshot.uniqueFeedbackId,
            )}`;
            editAudioBlob = undefined; // ✅ Clear stale blob
            console.log("✅ Constructed API URL:", editAudioUrl);
          }
          // ✅ CASE 5: Only has uniqueFeedbackId - construct API URL
          else if (!editAudioUrl && screenshot.uniqueFeedbackId) {
            console.log(
              "🎵 Case 5: No URL but has feedbackId - constructing API URL",
            );
            editAudioUrl = `${API_URLS.GET_FILE_VOICE(
              screenshot.uniqueFeedbackId,
            )}`;
            editAudioBlob = undefined;
            console.log("✅ Constructed API URL:", editAudioUrl);
          }
        }

        setEditingScreenshot({
          ...screenshot,
          annotatedImage: screenshot.annotatedImage,
          originalImage: screenshot.originalImage,
          audioUrl: editAudioUrl,
          audioBlob: editAudioBlob, // ✅ Pass blob only if valid, undefined otherwise
        });
        setShowEditPreview(true);
      }
    },
    [screenshots],
  );

  const handleEditRecording = useCallback(
    (recordingId: string) => {
      console.log("✏️ Edit recording requested:", recordingId);
      const recording = recordings.find((r) => r.id === recordingId);

      if (!recording) {
        console.error("Recording not found:", recordingId);
        return;
      }

      console.log("✏️ Recording data being passed to editor:", {
        id: recording.id,
        hasDataUrl: !!recording.dataUrl,
        hasRecordingBlob: !!recording.recordingBlob,
        blobSize: recording.recordingBlob?.size || 0,
        hasThumbnailUrl: !!recording.thumbnailUrl,
        dataUrlPreview: recording.dataUrl?.substring(0, 50),
        dataUrlType: recording.dataUrl?.startsWith("blob:")
          ? "blob"
          : recording.dataUrl?.startsWith("http")
            ? "remote"
            : "unknown",
      });

      // ✅ FIX: Better type checking and condition handling
      const hasValidBlob =
        recording.recordingBlob && recording.recordingBlob.size > 0;
      const hasDataUrl = !!recording.dataUrl;
      const isBlobUrl = recording.dataUrl?.startsWith("blob:") || false;
      const isRemoteUrl = recording.dataUrl?.startsWith("http") || false;

      // ✅ CASE 1: We have a valid blob AND it's a blob URL (immediate edit after recording)
      if (hasValidBlob && isBlobUrl && recording.recordingBlob) {
        console.log("✅ CASE 1: Creating fresh blob URL for immediate edit");

        // Revoke old blob URL if it exists
        try {
          URL.revokeObjectURL(recording.dataUrl);
          console.log("🧹 Revoked old blob URL");
        } catch (e) {
          console.warn("Could not revoke old blob URL:", e);
        }

        // Create fresh blob URL
        const freshBlobUrl = URL.createObjectURL(recording.recordingBlob);
        console.log(
          "✅ Fresh blob URL created:",
          freshBlobUrl.substring(0, 50),
        );

        setEditingRecording({
          ...recording,
          dataUrl: freshBlobUrl,
        });
        setShowEditRecordingPreview(true);
      }
      // ✅ CASE 2: We have a valid blob AND a remote URL (after save, before refresh)
      else if (hasValidBlob && isRemoteUrl && recording.recordingBlob) {
        console.log(
          "✅ CASE 2: We have both blob and remote URL, creating fresh blob URL",
        );

        const freshBlobUrl = URL.createObjectURL(recording.recordingBlob);
        console.log(
          "✅ Fresh blob URL created:",
          freshBlobUrl.substring(0, 50),
        );

        setEditingRecording({
          ...recording,
          dataUrl: freshBlobUrl, // Use blob URL for better performance
        });
        setShowEditRecordingPreview(true);
      }
      // ✅ CASE 3: We only have a remote URL (after refresh) - LOAD THE BLOB
      else if (!hasValidBlob && isRemoteUrl) {
        console.log("✅ CASE 3: Loading blob from remote URL after refresh");

        // Show preview immediately with loading state
        setEditingRecording({
          ...recording,
          recordingBlob: new Blob([], { type: "video/mp4" }), // Empty blob as placeholder
        });
        setShowEditRecordingPreview(true);

        // Load blob asynchronously in the background
        (async () => {
          try {
            console.log(
              "📹 Fetching recording blob from:",
              recording.dataUrl?.substring(0, 50),
            );
            const blobResponse = await fetch(recording.dataUrl);

            if (!blobResponse.ok) {
              throw new Error(`Failed to fetch video: ${blobResponse.status}`);
            }

            const validBlob = await blobResponse.blob();
            console.log("✅ Recording blob loaded successfully:", {
              size: validBlob.size,
              type: validBlob.type,
            });

            // Create blob URL for better playback
            const freshBlobUrl = URL.createObjectURL(validBlob);
            console.log(
              "✅ Fresh blob URL created from fetched blob:",
              freshBlobUrl.substring(0, 50),
            );

            // Update recordings state with the loaded blob
            setRecordings((prev) =>
              prev.map((r) =>
                r.id === recordingId
                  ? { ...r, recordingBlob: validBlob, dataUrl: freshBlobUrl }
                  : r,
              ),
            );

            // Update editing state with the loaded blob
            setEditingRecording((prev) =>
              prev
                ? { ...prev, recordingBlob: validBlob, dataUrl: freshBlobUrl }
                : null,
            );
          } catch (error) {
            console.error("❌ Failed to reload recording blob:", error);
            setShowEditRecordingPreview(false);
            setEditingRecording(null);
            alert("Failed to load recording for editing. Please try again.");
          }
        })();
      }
      // ✅ CASE 4: We have a valid blob but no dataUrl at all (edge case)
      else if (hasValidBlob && !hasDataUrl && recording.recordingBlob) {
        console.log(
          "✅ CASE 4: We have blob but no dataUrl, creating fresh blob URL",
        );

        const freshBlobUrl = URL.createObjectURL(recording.recordingBlob);
        console.log(
          "✅ Fresh blob URL created:",
          freshBlobUrl.substring(0, 50),
        );

        setEditingRecording({
          ...recording,
          dataUrl: freshBlobUrl,
        });
        setShowEditRecordingPreview(true);
      }
      // ✅ CASE 5: No valid data at all
      else {
        console.error(
          "❌ CASE 5: Cannot edit recording - no valid data available:",
          {
            hasValidBlob,
            hasDataUrl,
            isBlobUrl,
            isRemoteUrl,
            dataUrl: recording.dataUrl?.substring(0, 100),
          },
        );
        alert("Recording data is not available. Please try again.");
      }
    },
    [recordings],
  );

  // NEW: Handle screenshot edit submission
  const handleEditSubmit = useCallback(
    async (submission: ScreenshotSubmission) => {
      console.log(
        "✏️ Screenshot edit submitted - Updates uploaded to SAME feedback ID",
      );

      if (!editingScreenshot) return;

      const feedbackIdToUse = editingScreenshot.uniqueFeedbackId || feedbackId;

      if (!feedbackIdToUse) {
        console.error("❌ No feedback ID available for edit upload");
        setFeedbackError("Cannot update screenshot: No feedback ID found");
        return;
      }

      console.log("✏️ Edit completed with feedback ID:", feedbackIdToUse);

      // ✅ UPDATE STATE IMMEDIATELY - instant UI update
      let newAudioUrl = submission.audioUrl;
      if (submission.audioBlob) {
        if (editingScreenshot.audioUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(editingScreenshot.audioUrl);
        }
        newAudioUrl = URL.createObjectURL(submission.audioBlob);
      }

      setScreenshots((prev) =>
        prev.map((s) =>
          s.id === editingScreenshot.id
            ? {
                ...s,
                annotatedImage: submission.annotatedImage,
                originalImage: submission.originalImage,
                feedback: submission.feedback,
                hasAudio: submission.hasAudio,
                audioUrl: newAudioUrl,
                audioBlob: submission.audioBlob,
                audioFilename: submission.audioFilename,
                timestamp: Date.now(),
              }
            : s,
        ),
      );

      setShowEditPreview(false);
      setEditingScreenshot(null);

      console.log(
        "✅ Screenshot updated instantly in local state and uploaded to backend",
      );
    },
    [editingScreenshot, feedbackId],
  );

  const handleEditRecordingSubmit = useCallback(
    async (submission: RecordingSubmission) => {
      console.log("✏️ Recording edit submitted");

      if (!editingRecording) return;

      const feedbackIdToUse = editingRecording.uniqueFeedbackId || feedbackId;

      // ✅ UPDATE STATE IMMEDIATELY
      setRecordings((prev) =>
        prev.map((r) =>
          r.id === editingRecording.id
            ? {
                ...r,
                dataUrl: submission.dataUrl || r.dataUrl,
                feedback: submission.feedback,
                recordingBlob: submission.recordingBlob || r.recordingBlob,
                thumbnailUrl: submission.thumbnailUrl || r.thumbnailUrl,
                timestamp: Date.now(),
              }
            : r,
        ),
      );

      setShowEditRecordingPreview(false);
      setEditingRecording(null);

      console.log("✅ Recording updated instantly in local state");

      // ✅ Background upload
      if (feedbackIdToUse && submission.feedback?.trim()) {
        ApiService.uploadMessage(
          submission.feedback, // ✅ CRITICAL: Send HTML content
          feedbackIdToUse,
        ).catch((err) =>
          console.error("Background message upload failed:", err),
        );
      }
    },
    [editingRecording, feedbackId],
  );

  const handleRemoveRecording = useCallback(
    (recordingId: string) => {
      console.log("🗑️ Delete recording requested:", recordingId);
      const recording = recordings.find((r) => r.id === recordingId);
      setItemToDelete({
        id: recordingId,
        type: "recording",
        uniqueFeedbackId: recording?.uniqueFeedbackId,
      });
      setShowDeleteConfirm(true);
    },
    [recordings],
  );

  const handlePlayRecording = useCallback(
    (recordingId: string) => {
      console.log("▶️ Playing recording:", recordingId);

      const recording = recordings.find((r) => r.id === recordingId);
      if (!recording || !recording.dataUrl) {
        console.error("Recording not found or no dataUrl available");
        return;
      }

      const video = document.createElement("video");
      video.src = recording.dataUrl;
      video.controls = true;
      video.autoplay = true;
      video.style.maxWidth = "90vw";
      video.style.maxHeight = "90vh";

      const modal = document.createElement("div");
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      `;

      const closeButton = document.createElement("button");
      closeButton.textContent = "×";
      closeButton.style.cssText = `
        position: absolute;
        top: 20px;
        right: 30px;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        font-size: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      closeButton.onclick = () => {
        document.body.removeChild(modal);
        video.pause();
        if (video.src.startsWith("blob:")) {
          URL.revokeObjectURL(video.src);
        }
      };

      modal.appendChild(video);
      modal.appendChild(closeButton);
      document.body.appendChild(modal);

      modal.onclick = (e) => {
        if (e.target === modal) {
          closeButton.click();
        }
      };
    },
    [recordings],
  );

  // Delete confirmation handlers
  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;

    const { id, type, uniqueFeedbackId } = itemToDelete;

    console.log("🗑️ Confirming delete:", { id, type, uniqueFeedbackId });

    // ✅ MOVED: Close popup immediately (moved to top)
    setShowDeleteConfirm(false);
    setItemToDelete(null);

    // Delete from backend if we have a unique feedback ID
    if (uniqueFeedbackId) {
      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          console.error("No access token available for deletion");
        } else {
          // Extract numeric ID from feedback ID
          const numericFeedbackId = String(uniqueFeedbackId)
            .replace(/^feedback_/, "")
            .trim();

          console.log("🗑️ Deleting from database:", numericFeedbackId);

          const response = await fetch(
            API_URLS.DELETE_FILE(numericFeedbackId),
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          );

          if (!response.ok) {
            console.error("Failed to delete from database:", response.status);
          } else {
            console.log("✅ Successfully deleted from database");
          }
        }
      } catch (error) {
        console.error("❌ Error deleting from database:", error);
      }
    }

    // Remove from local state
    if (type === "screenshot") {
      setScreenshots((prev) => {
        const screenshot = prev.find((s) => s.id === id);
        if (screenshot?.audioUrl && screenshot.audioUrl.startsWith("blob:")) {
          URL.revokeObjectURL(screenshot.audioUrl);
        }
        return prev.filter((s) => s.id !== id);
      });
      console.log("✅ Screenshot removed from local state");
    } else if (type === "recording") {
      setRecordings((prev) => {
        const recording = prev.find((r) => r.id === id);
        if (recording?.dataUrl && recording.dataUrl.startsWith("blob:")) {
          URL.revokeObjectURL(recording.dataUrl);
        }
        return prev.filter((r) => r.id !== id);
      });
      console.log("✅ Recording removed from local state");
    }

    // ✅ NEW: Show success message
    setSuccessMessageText(
      `${
        type === "screenshot" ? "Screenshot" : "Recording"
      } deleted successfully!`,
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, [itemToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  }, []);

  // ✅ NEW: Handle share button click - show confirmation popup
  const handleShareClick = useCallback(() => {
    console.log("📤 Share button clicked - showing confirmation");
    setShowSubmitConfirm(true);
  }, []);

  // ✅ NEW: Handle submit confirmation - show share popup
  const handleSubmitConfirm = useCallback(() => {
    console.log("✅ Submit confirmed - opening share popup");
    setShowSubmitConfirm(false);
    setIsSharePopupOpen(true);
  }, []);

  // ✅ NEW: Handle submit cancel
  const handleSubmitCancel = useCallback(() => {
    console.log("❌ Submit cancelled");
    setShowSubmitConfirm(false);
  }, []);

  // ✅ NEW: Handle share with developer - with navigation to home
  const handleShareEmails = useCallback(
    async (emails: string[]) => {
      if (isSharing) {
        console.warn("🚫 Share already in progress");
        return;
      }

      setIsSharing(true);
      setIsSharePopupOpen(false);

      // Clean up emails
      const uniqueEmails = Array.from(
        new Set(emails.map((email) => email.trim().toLowerCase())),
      ).filter((email) => email.length > 0);

      if (uniqueEmails.length === 0) {
        setIsSharing(false);
        console.error("No valid email addresses provided");
        return;
      }

      console.log("🎯 Sharing card with developers:", {
        cardId,
        emails: uniqueEmails,
        feedbackCount: totalFeedbackItems,
      });

      try {
        const result = await shareFeedbackWithDeveloper(
          cardId,
          uniqueEmails,
          workspaceId || undefined,
        );

        if (result.success && result.data) {
          console.log("✅ Card shared successfully:", result.data);

          // Show success message briefly
          setSuccessMessageText(
            `Feedback shared with ${uniqueEmails.length} developer(s) successfully!`,
          );
          setShowSuccessMessage(true);

          // ✅ UPDATED: Navigate to home screen with proper share success data
          setTimeout(() => {
            console.log(
              "🏠 Navigating to home screen after successful share from FeedbackWorkspace",
            );

            // Call onBack with complete share success data (same as ViewListFeedback)
            onBack?.({
              cardDeleted: false,
              deletedCardId: 0,
              deletedCardName: "",
              activeTab: "all", // Navigate to "all" (created) tab
              shareSuccess: {
                cardId: cardId,
                feedbackName: feedbackName,
                emails: uniqueEmails,
                feedbackCount: totalFeedbackItems,
              },
            });
          }, 1500); // Wait 1.5 seconds to show success message
        } else {
          throw new Error(result.error || "Share failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        console.error("❌ Share failed:", errorMessage);

        // Show error message
        setFeedbackError(`Failed to share: ${errorMessage}`);
        setTimeout(() => setFeedbackError(""), 5000);

        // Re-open popup on error
        setIsSharePopupOpen(true);
        setIsSharing(false);
      }
    },
    [cardId, workspaceId, totalFeedbackItems, isSharing, feedbackName, onBack],
  );

  // ✅ NEW: Enhanced Save & Exit that captures latest state
  const handleSaveAndExitWithLatestData = useCallback(() => {
    console.log("💾 Saving with LATEST data before navigation");

    // ✅ Get the LATEST state from React (captures any recent edits)
    const latestScreenshots = screenshots;
    const latestRecordings = recordings;

    // Create complete feedback items from LATEST state
    const latestFeedbackItems: FeedbackItem[] = feedbackItemsOrder
      .map((itemKey) => {
        if (itemKey.startsWith("screenshot-")) {
          const screenshot = latestScreenshots.find(
            (s) => `screenshot-${s.id}` === itemKey,
          );
          if (!screenshot) return null;

          const item: FeedbackItem = {
            id: screenshot.id,
            type: "screenshot" as const,
            timestamp: screenshot.timestamp,
            order: feedbackItemsOrder.indexOf(itemKey),
            annotatedImage: screenshot.annotatedImage,
            originalImage: screenshot.originalImage,
            feedback: screenshot.feedback,
            hasAudio: screenshot.hasAudio || false,
            audioUrl: screenshot.audioUrl,
            audioFilename: screenshot.audioFilename,
            uniqueFeedbackId: screenshot.uniqueFeedbackId || screenshot.id,
            apiFeedbackId: screenshot.apiFeedbackId,
          };
          return item;
        } else if (itemKey.startsWith("recording-")) {
          const recording = latestRecordings.find(
            (r) => `recording-${r.id}` === itemKey,
          );
          if (!recording) return null;

          const item: FeedbackItem = {
            id: recording.id,
            type: "recording" as const,
            timestamp: recording.timestamp,
            order: feedbackItemsOrder.indexOf(itemKey),
            dataUrl: recording.dataUrl,
            recordingBlob: recording.recordingBlob,
            duration: recording.duration,
            feedback: recording.feedback,
            thumbnailUrl: recording.thumbnailUrl,
            uniqueFeedbackId: recording.uniqueFeedbackId || recording.id,
            apiFeedbackId: recording.apiFeedbackId,
          };

          return item;
        }
        return null;
      })
      .filter((item): item is FeedbackItem => item !== null);

    const workspaceStateData = {
      screenshots: latestScreenshots,
      recordings: latestRecordings,
      feedbackItemsOrder,
      url,
      iframeHistory,
      iframeHistoryIndex,
      showIframe,
      feedbackId,
      isUrlLocked,
    };

    console.log("✅ Passing FRESH data to ViewListFeedback:", {
      screenshotCount: latestScreenshots.length,
      recordingCount: latestRecordings.length,
      totalItems: latestFeedbackItems.length,
    });

    // Pass fresh data to parent
    onSaveAndExit(latestFeedbackItems, workspaceStateData);
  }, [
    screenshots,
    recordings,
    feedbackItemsOrder,
    url,
    iframeHistory,
    iframeHistoryIndex,
    showIframe,
    feedbackId,
    isUrlLocked,
    onSaveAndExit,
  ]);

  // Updated save and exit handler
  const handleSaveAndExit = useCallback(() => {
    console.log("💾 FeedbackWorkspace - Saving and exiting with data:", {
      screenshots: screenshots.length,
      recordings: recordings.length,
      totalItems: allFeedbackItems.length,
      feedbackId: feedbackId,
      isUrlLocked: isUrlLocked,
      uniqueFeedbackIds: [
        ...screenshots.map((s) => s.uniqueFeedbackId).filter(Boolean),
        ...recordings.map((r) => r.uniqueFeedbackId).filter(Boolean),
      ],
    });

    const workspaceStateData = {
      screenshots,
      recordings,
      feedbackItemsOrder,
      url,
      iframeHistory,
      iframeHistoryIndex,
      showIframe,
      feedbackId,
      isUrlLocked,
    };

    onSaveAndExit(allFeedbackItems, workspaceStateData);

    // ✅ NEW: Trigger draft refresh when saving and exiting
    console.log("♻️ Triggering refresh events after save and exit...");
    window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
    localStorage.setItem("draft_saved_timestamp", Date.now().toString());
  }, [
    allFeedbackItems,
    feedbackItemsOrder,
    screenshots,
    recordings,
    url,
    iframeHistory,
    iframeHistoryIndex,
    showIframe,
    feedbackId,
    isUrlLocked,
    onSaveAndExit,
  ]);

  const handleSaveDraft = useCallback(() => {
    console.log("📝 FeedbackWorkspace - Saving draft with data:", {
      screenshots: screenshots.length,
      recordings: recordings.length,
      totalItems: allFeedbackItems.length,
      feedbackId: feedbackId,
      isUrlLocked: isUrlLocked,
    });

    const workspaceStateData = {
      screenshots,
      recordings,
      feedbackItemsOrder,
      url,
      iframeHistory,
      iframeHistoryIndex,
      showIframe,
      feedbackId,
      isUrlLocked,
      shouldRefreshDrafts: true,
    };

    // ✅ Pass complete data to parent
    onSaveDraft(allFeedbackItems, workspaceStateData);

    // ✅ NEW: Trigger draft tab refresh immediately after save
    console.log("♻️ Triggering draft refresh after save...");
    window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
    localStorage.setItem("draft_saved_timestamp", Date.now().toString());
  }, [
    allFeedbackItems,
    feedbackItemsOrder,
    screenshots,
    recordings,
    url,
    iframeHistory,
    iframeHistoryIndex,
    showIframe,
    feedbackId,
    isUrlLocked,
    onSaveDraft,
  ]);

  const handleNewTabClick = useCallback(() => {
    setIsShaking(true);
    setTimeout(() => {
      setIsShaking(false);
    }, 820); // Match the animation duration
  }, []);

  const handleGoToHome = useCallback(() => {
    if (onMinimize) {
      console.log("🔽 Minimizing workspace to PiP mode with live data:", {
        feedbackCount: allFeedbackItems.length,
        screenshots: screenshots.length,
        recordings: recordings.length,
      });

      const liveWorkspaceState = {
        screenshots,
        recordings,
        feedbackItemsOrder,
        url,
        iframeHistory,
        iframeHistoryIndex,
        showIframe,
        feedbackId,
        isUrlLocked,
      };

      onMinimize(allFeedbackItems, liveWorkspaceState);
    }
  }, [
    onMinimize,
    allFeedbackItems,
    screenshots,
    recordings,
    feedbackItemsOrder,
    url,
    iframeHistory,
    iframeHistoryIndex,
    showIframe,
    feedbackId,
    isUrlLocked,
  ]);

  const handleToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  // delete handler function:
  const handleDeleteCard = useCallback(async () => {
    if (!cardId) {
      console.error("No card ID available for deletion");
      return;
    }

    setIsDeletingCard(true);

    try {
      console.log("🗑️ Deleting card:", cardId);

      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const response = await fetch(API_URLS.DELETE_CARD(cardId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete card: ${errorText}`);
      }

      console.log("✅ Card deleted successfully");

      // Close confirmation popup
      setShowDeleteCardConfirm(false);

      // ✅ NEW: Navigate IMMEDIATELY - toast will show on HomeScreen
      onBack({
        cardDeleted: true,
        deletedCardId: cardId,
        deletedCardName: feedbackName,
        activeTab: "draft",
      });
    } catch (error) {
      console.error("❌ Error deleting card:", error);
      alert(
        `Failed to delete card: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    } finally {
      setIsDeletingCard(false);
    }
  }, [cardId, feedbackName, onBack]);

  const handleDeleteButtonClick = useCallback(() => {
    setShowDeleteCardConfirm(true);
  }, []);

  // Sync input URL with current iframe URL when navigating
  useEffect(() => {
    if (currentIframeUrl) {
      setInputUrl(currentIframeUrl);
      setUrl(currentIframeUrl);
    }
  }, [currentIframeUrl]);

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      screenshots.forEach((screenshot) => {
        if (screenshot.audioUrl && screenshot.audioUrl.startsWith("blob:")) {
          URL.revokeObjectURL(screenshot.audioUrl);
        }
      });
      recordings.forEach((recording) => {
        if (recording.dataUrl && recording.dataUrl.startsWith("blob:")) {
          URL.revokeObjectURL(recording.dataUrl);
        }
      });
    };
  }, [screenshots, recordings]);

  useEffect(() => {
    if (loadingItems.length === 0) return;

    const interval = setInterval(() => {
      setLoadingItems((prev) =>
        prev.map((item) => {
          // Slowly progress from 0% to 80% over ~3 seconds
          if (item.progress < 80) {
            return {
              ...item,
              progress: Math.min(item.progress + 2, 80), // Increment by 2% each 100ms
            };
          }
          // Stop at 80% until backend completes
          return item;
        }),
      );
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [loadingItems.length]);

  // Initialize audio cache for better audio handling
  useEffect(() => {
    if (!(window as any).__audio_cache) {
      (window as any).__audio_cache = {};
    }
  }, []);

  // Track when focus moves into the iframe so shortcuts pause
  useEffect(() => {
    const handleWindowBlur = () => {
      if (document.activeElement?.tagName === "IFRAME") {
        setIsIframeFocused(true);
      }
    };
    const handleWindowFocus = () => {
      setIsIframeFocused(false);
    };
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);


  // Auto-show sidebar and top bar for 1s when entering fullscreen so user notices them
  useEffect(() => {
    if (!isWorkspaceFullscreen) {
      setShowFloatingSidebar(false);
      setShowFloatingTopBar(false);
      return;
    }
    setShowFloatingSidebar(true);
    setShowFloatingTopBar(true);
    const timer = setTimeout(() => {
      setShowFloatingSidebar(false);
      setShowFloatingTopBar(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [isWorkspaceFullscreen]);

  // Reset toolbox to default position each time fullscreen is opened
  useEffect(() => {
    if (isWorkspaceFullscreen) {
      setToolboxPos(null);
    }
  }, [isWorkspaceFullscreen]);

  // Sync recording-active state so sidebar/top-bar are hidden during screen recording
  useEffect(() => {
    const handler = (e: Event) => {
      setIsRecordingActive((e as CustomEvent<{ isRecording: boolean }>).detail.isRecording);
    };
    window.addEventListener('feebdak-recording-state', handler);
    return () => window.removeEventListener('feebdak-recording-state', handler);
  }, []);

  // Sync drawing mode state so toolbox drag and cursor are disabled while annotating
  useEffect(() => {
    const handler = (e: Event) => {
      setIsDrawingMode((e as CustomEvent<{ drawingMode: boolean }>).detail.drawingMode);
    };
    window.addEventListener('feebdak-drawing-mode', handler);
    return () => window.removeEventListener('feebdak-drawing-mode', handler);
  }, []);

  // Global mouse handlers for dragging the fullscreen toolbox
  useEffect(() => {
    // Shared commit logic — reads visual position from getBoundingClientRect (includes transform)
    const commitPosition = () => {
      const el = toolboxRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const finalLeft = Math.max(0, Math.min(window.innerWidth - el.offsetWidth, rect.left));
      const finalTop  = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, rect.top));

      // Snap to origin if dropped within 100px of the default bottom-left corner
      const originLeft = 8;
      const originTop  = window.innerHeight - el.offsetHeight - 8;
      const dist = Math.sqrt(Math.pow(finalLeft - originLeft, 2) + Math.pow(finalTop - originTop, 2));

      if (dist < 100) {
        el.style.cursor     = '';
        el.style.transition = '';
        el.style.transform  = '';
        el.style.willChange = 'auto';
        el.style.top        = '';
        el.style.right      = '';
        el.style.left       = '8px';
        el.style.bottom     = '8px';
        setToolboxPos(null);
        return;
      }

      el.style.cursor     = '';
      el.style.left       = `${finalLeft}px`;
      el.style.top        = `${finalTop}px`;
      el.style.right      = 'auto';
      el.style.bottom     = 'auto';
      el.style.transform  = '';
      el.style.transition = '';
      el.style.willChange = 'auto';
      setToolboxPos({ left: finalLeft, top: finalTop });
    };

    const handleMouseMove = (e: MouseEvent) => {
      const drag = toolboxDragRef.current;
      if (!drag.isDragging) return;

      // Mouse button was released outside the browser window — stop drag immediately
      if (e.buttons === 0) {
        drag.isDragging = false;
        document.body.style.cursor    = '';
        document.body.style.userSelect = '';
        commitPosition();
        return;
      }

      const el = toolboxRef.current;
      if (!el) return;

      const clampedLeft = Math.max(0, Math.min(window.innerWidth  - el.offsetWidth,  drag.startLeft + (e.clientX - drag.startX)));
      const clampedTop  = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, drag.startTop  + (e.clientY - drag.startY)));

      // GPU-accelerated transform — zero React re-renders during drag
      el.style.transform = `translate(${clampedLeft - drag.startLeft}px, ${clampedTop - drag.startTop}px)`;
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!toolboxDragRef.current.isDragging) return;
      toolboxDragRef.current.isDragging  = false;
      document.body.style.cursor         = '';
      document.body.style.userSelect     = '';

      const el   = toolboxRef.current;
      if (!el) return;
      const drag = toolboxDragRef.current;

      // Movement < 8px means it was a click on the drag handle, not a real drag —
      // bail without touching bottom/top styles so the toolbox stays put.
      if (Math.abs(e.clientX - drag.startX) < 8 && Math.abs(e.clientY - drag.startY) < 8) {
        el.style.cursor     = '';
        el.style.transition = '';
        el.style.transform  = '';
        el.style.willChange = 'auto';
        return;
      }

      commitPosition();
    };

    // If the user alt-tabs or moves the mouse outside the browser while dragging,
    // the mouseup fires outside our window and we'd never stop. Catch it here.
    const handleWindowBlur = () => {
      if (!toolboxDragRef.current.isDragging) return;
      toolboxDragRef.current.isDragging  = false;
      document.body.style.cursor         = '';
      document.body.style.userSelect     = '';
      commitPosition();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup",   handleMouseUp);
    window.addEventListener("blur",        handleWindowBlur);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup",   handleMouseUp);
      window.removeEventListener("blur",        handleWindowBlur);
    };
  }, []);

  // Add keyboard shortcut for PiP mode
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // ESC exits fullscreen (but not while recording — let ScreenRecordingManager handle it)
      if (event.key === "Escape" && isWorkspaceFullscreen && !isRecordingActive) {
        event.preventDefault();
        setIsWorkspaceFullscreen(false);
        return;
      }

      if (showEditPreview || showEditRecordingPreview || isAnyPreviewOpen || isIframeFocused) {
        return;
      }

      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA";

      // F — toggle workspace fullscreen (hide left bar + header)
      if ((event.key === "f" || event.key === "F") && !isTyping) {
        event.preventDefault();
        setIsWorkspaceFullscreen((prev) => !prev);
        return;
      }

      // Check if 'H' key is pressed (case insensitive)
      if ((event.key === "h" || event.key === "H") && !isTyping) {
        event.preventDefault();
        handleGoToHome();
      }

      // Check if 'M' key is pressed for mute toggle
      if ((event.key === "m" || event.key === "M") && !isTyping) {
        event.preventDefault();
        handleToggleMute();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    handleGoToHome,
    handleToggleMute,
    showEditPreview,
    showEditRecordingPreview,
    isAnyPreviewOpen,
    isIframeFocused,
    isWorkspaceFullscreen,
    isRecordingActive,
  ]);

  useEffect(() => {
    localStorage.setItem("screenshot_sound_muted", String(isMuted));
  }, [isMuted]);

  // Global keyboard shortcut handler
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (showEditPreview || showEditRecordingPreview || isIframeFocused) {
        return;
      }

      // Ignore if typing in input fields OR inside TipTap editor
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.closest(".ProseMirror") ||
        target.classList.contains("ProseMirror")
      ) {
        return;
      }

      const hasValidUrl = showIframe && currentIframeUrl;

      if (event.key.toLowerCase() === "s") {
        event.preventDefault();
        event.stopPropagation();

        if (!hasValidUrl) {
          alert("Please load a website URL first");
          return;
        }

        window.dispatchEvent(new CustomEvent("trigger-screenshot"));
      } else if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        event.stopPropagation();

        if (!hasValidUrl) {
          alert("Please load a website URL first");
          return;
        }

        window.dispatchEvent(new CustomEvent("trigger-recording"));
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, [showIframe, currentIframeUrl, showEditPreview, showEditRecordingPreview, isIframeFocused]);

  useEffect(() => {
    return () => {
      // Clean up blob URL when component unmounts or editing screenshot changes
      if (
        editingScreenshot?.audioUrl &&
        editingScreenshot.audioUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(editingScreenshot.audioUrl);
        console.log("🧹 Cleaned up editing screenshot blob URL");
      }
    };
  }, [editingScreenshot?.audioUrl]);

  useEffect(() => {
    // Check if this is the first project and user hasn't seen the tour
    const tourCompleted = localStorage.getItem(getTourStorageKey());

    if (!tourCompleted && !hasSeenTour) {
      // Show tour after a brief delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setShowAppTour(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [hasSeenTour]);

  // ✅ FIXED: Show tour only for TRULY new projects
  useEffect(() => {
    let mounted = true;

    try {
      // Read from localStorage directly for most up-to-date data
      const stored = localStorage.getItem("tour_completed_projects");
      const completedProjects = stored
        ? new Set(JSON.parse(stored) as number[])
        : new Set<number>();

      console.log("🔍 Tour check for project:", cardId, {
        hasCompletedTour: completedProjects.has(cardId),
        allCompletedProjects: Array.from(completedProjects),
        showIframe,
        currentIframeUrl,
        totalFeedbackItems,
        loadingItems: loadingItems.length,
        hasSeenTour,
        hasInitialFeedbackItems: !!(
          initialFeedbackItems && initialFeedbackItems.length > 0
        ),
        hasInitialWorkspaceState: !!initialWorkspaceState,
      });

      // ✅ CRITICAL FIX: Check if this project has already completed the tour
      if (completedProjects.has(cardId)) {
        console.log("🎯 Tour already completed for this project:", cardId);
        return;
      }

      // Only show tour on the very first project ever created
      const tourEverCompleted = localStorage.getItem(getTourStorageKey());
      if (tourEverCompleted) {
        console.log("🎯 Tour already completed in a previous session — skipping");
        return;
      }

      // ✅ CRITICAL FIX: Only show tour for TRULY NEW projects
      // A new project has:
      // 1. No initial feedback items
      // 2. No initial workspace state (or minimal state)
      // 3. Browser navigation visible (no iframe)
      // 4. No feedback items yet

      const isBrowserNavigationVisible = !showIframe && !currentIframeUrl;
      const hasNoFeedbackYet =
        totalFeedbackItems === 0 && loadingItems.length === 0;

      // ✅ NEW: Check if this is truly a new project (not a draft being reopened)
      const isTrulyNewProject =
        (!initialFeedbackItems || initialFeedbackItems.length === 0) &&
        (!initialWorkspaceState || !initialWorkspaceState.feedbackId);

      console.log("🔍 Tour decision factors:", {
        isBrowserNavigationVisible,
        hasNoFeedbackYet,
        isTrulyNewProject,
        hasSeenTour,
        willShowTour:
          isBrowserNavigationVisible &&
          hasNoFeedbackYet &&
          isTrulyNewProject &&
          !hasSeenTour &&
          mounted,
      });

      if (
        isBrowserNavigationVisible &&
        hasNoFeedbackYet &&
        isTrulyNewProject && // ✅ This is the KEY check
        !hasSeenTour &&
        isTourStatusLoaded &&
        mounted
      ) {
        console.log(
          "🎓 Showing tour for NEW project (never opened before):",
          cardId,
        );
        // Show tour after a brief delay to ensure DOM is ready
        const timer = setTimeout(() => {
          if (mounted) {
            setShowAppTour(true);
          }
        }, 500);

        return () => {
          mounted = false;
          clearTimeout(timer);
        };
      } else {
        console.log("❌ Tour NOT shown - conditions not met");
      }
    } catch (error) {
      console.error("❌ Error in tour check:", error);
    }

    return () => {
      mounted = false;
    };
  }, [
    hasSeenTour,
    isTourStatusLoaded,
    cardId,
    tourCompletedProjects,
    showIframe,
    currentIframeUrl,
    totalFeedbackItems,
    loadingItems.length,
    initialFeedbackItems,
    initialWorkspaceState,
  ]);

  // ✅ NEW: Sync localStorage on mount to catch any missed updates
  useEffect(() => {
    try {
      const stored = localStorage.getItem("tour_completed_projects");
      if (stored) {
        const completedProjects = new Set(JSON.parse(stored) as number[]); // ✅ Type assertion
        setTourCompletedProjects(completedProjects);
        console.log(
          "🔄 Synced tour completion state from localStorage:",
          Array.from(completedProjects),
        );
      }
    } catch (error) {
      console.error("❌ Error syncing tour completion state:", error);
    }
  }, [cardId]);

  // Check server-side tour completion status (covers cross-device / new browser scenarios)
  useEffect(() => {
    getTourGuideStatus().then((completedKeys) => {
      if (completedKeys.includes(String(TOUR_GUIDE_KEYS.APP_TOUR))) {
        setHasSeenTour(true);
      }
      setIsTourStatusLoaded(true);
    });
  }, []);

  // ✅ NEW: Auto-refresh when upload completes in workspace
  useEffect(() => {
    const handleUploadComplete = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId: uploadCardId, feedbackId: uploadedFeedbackId } =
        event.detail;

      if (uploadCardId === cardId) {
        console.log("⚡ Upload completed in workspace, auto-updating UI");

        // ✅ Option 1: Force re-render by updating a dummy state
        // (Not recommended - your state already updates automatically)

        // ✅ Option 2: Refresh the specific item if needed
        // Find and update the item with the uploaded feedback ID
        setScreenshots((prev) =>
          prev.map((s) =>
            s.uniqueFeedbackId === uploadedFeedbackId
              ? { ...s, timestamp: Date.now() } // Force refresh
              : s,
          ),
        );

        setRecordings((prev) =>
          prev.map((r) =>
            r.uniqueFeedbackId === uploadedFeedbackId
              ? { ...r, timestamp: Date.now() } // Force refresh
              : r,
          ),
        );
      }
    };

    window.addEventListener(
      "upload-item-completed",
      handleUploadComplete as EventListener,
    );

    return () => {
      window.removeEventListener(
        "upload-item-completed",
        handleUploadComplete as EventListener,
      );
    };
  }, [cardId]);

  const handleCloseTour = () => {
    console.log("✅ Tour completed/skipped for project:", cardId);

    setShowAppTour(false);
    setHasSeenTour(true);

    markTourGuideDone(TOUR_GUIDE_KEYS.APP_TOUR);

    // ✅ CRITICAL FIX: Update localStorage with multiple verification steps
    try {
      // Step 1: Read existing data
      const stored = localStorage.getItem("tour_completed_projects");
      console.log("📖 Reading existing tour data:", stored);

      const existingProjects = stored
        ? new Set(JSON.parse(stored) as number[])
        : new Set<number>();

      console.log(
        "📋 Existing completed projects:",
        Array.from(existingProjects),
      );

      // Step 2: Add current project
      existingProjects.add(cardId);
      console.log("➕ Added cardId:", cardId);
      console.log("📋 Updated projects list:", Array.from(existingProjects));

      // Step 3: Convert to array for storage
      const projectsArray = Array.from(existingProjects);
      const jsonString = JSON.stringify(projectsArray);
      console.log("💾 Saving to localStorage:", jsonString);

      // Step 4: Save to localStorage
      localStorage.setItem("tour_completed_projects", jsonString);
      localStorage.setItem(getTourStorageKey(), "true");

      // Step 5: IMMEDIATE verification read
      const immediateVerification = localStorage.getItem(
        "tour_completed_projects",
      );
      console.log(
        "✅ Immediate verification - localStorage contains:",
        immediateVerification,
      );

      if (immediateVerification !== jsonString) {
        console.error(
          "❌ CRITICAL: localStorage save failed! Expected:",
          jsonString,
          "Got:",
          immediateVerification,
        );
        // Try saving again
        localStorage.setItem("tour_completed_projects", jsonString);
        const secondVerification = localStorage.getItem(
          "tour_completed_projects",
        );
        console.log("🔄 Second attempt verification:", secondVerification);
      }

      // Step 6: Update React state AFTER successful localStorage save
      setTourCompletedProjects(existingProjects);
      console.log("✅ React state updated");

      // Step 7: Final verification after a small delay
      setTimeout(() => {
        const finalCheck = localStorage.getItem("tour_completed_projects");
        console.log("🔍 Final check (100ms later):", finalCheck);

        if (finalCheck) {
          const parsed = JSON.parse(finalCheck) as number[];
          if (!parsed.includes(cardId)) {
            console.error(
              "❌ CRITICAL: cardId not in localStorage after save!",
            );
          } else {
            console.log(
              "✅ Tour completion successfully persisted for cardId:",
              cardId,
            );
          }
        }
      }, 100);
    } catch (error) {
      console.error("❌ Error saving tour completion:", error);
      // Emergency fallback - try direct save
      try {
        localStorage.setItem(
          "tour_completed_projects",
          JSON.stringify([cardId]),
        );
        console.log("🚨 Emergency fallback save attempted");
      } catch (fallbackError) {
        console.error("❌ Emergency fallback also failed:", fallbackError);
      }
    }
  };

  // Browser Navigation Component
  const BrowserNavigation = useMemo(
    () => (
      <div className="flex items-center gap-1 sm:gap-2 xl:gap-1 2xl:gap-2 4xl:gap-3 flex-1">
        <form
          onSubmit={handleUrlSubmit}
          className="flex-1 ml-2 sm:ml-3 xl:ml-4 2xl:ml-5 4xl:ml-6 relative"
          data-tour="url-input"
        >
          <div
            className={`flex items-center bg-white border border-[#000000] rounded-[8px] sm:rounded-[10px] 3xl:rounded-[12px] px-2 sm:px-3 xl:px-4 2xl:px-4 4xl:px-5 py-1.5 sm:py-2 xl:py-[12px] 2xl:py-[12px] 4xl:py-3 w-full  ${
              isUrlLocked ? "bg-gray-50 border-gray-200" : ""
            }`}
          >
            <img
              src="https://www.google.com/favicon.ico"
              alt=""
              className="w-3 h-3 sm:w-4 sm:h-4 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 mr-1.5 sm:mr-2 xl:mr-2 4xl:mr-4 flex-shrink-0"
            />
            <div className="w-px bg-[#B7BBCA] self-stretch my-1 mr-1.5 sm:mr-2 xl:mr-2 4xl:mr-4"></div>
            <input
              ref={urlInputRef}
              type="text"
              placeholder={
                isUrlLocked
                  ? "URL locked - feedback items added"
                  : "Paste your url here to mirror"
              }
              value={inputUrl}
              onChange={handleUrlInputChange}
              onKeyDown={handleUrlInputKeyDown}
              onFocus={handleUrlInputFocus}
              className={`flex-1 bg-transparent border-none outline-none text-[10px] sm:text-xs xl:text-xs 2xl:text-sm 3xl:text-base 4xl:text-lg font-inter text-gray-800 placeholder-gray-400 min-w-0  ${
                isUrlLocked
                  ? "cursor-not-allowed text-gray-500 placeholder-gray-500"
                  : ""
              }`}
              
              autoComplete="off"
              disabled={isCreatingFeedback || isUrlLocked}
              readOnly={isUrlLocked}
              title={
                isUrlLocked
                  ? "URL cannot be changed after adding feedback items. Use reload button to refresh the current page."
                  : ""
              }
            />
            <button
              type="submit"
              className="ml-2 outline-none hover:opacity-70 transition-opacity disabled:opacity-50"
              aria-label={
                isUrlLocked ? "Reload current URL" : "Navigate to URL"
              }
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              disabled={isCreatingFeedback}
              title={isUrlLocked ? "Reload current page" : "Navigate to URL"}
            >
              {isCreatingFeedback ? (
                <div className="w-3 h-3 sm:w-4 sm:h-4 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 animate-spin">
                  <svg
                    className="w-full h-full"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              ) : isUrlLocked ? (
                <Reload className="w-3 h-3 sm:w-4 sm:h-4 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 text-gray-400 flex-shrink-0" />
              ) : (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* ✅ NEW: URL Suggestions Dropdown */}
          {showUrlDropdown && filteredUrls.length > 0 && !isUrlLocked && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
            >
              <div
                className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Recently used
              </div>
              {filteredUrls.map((url, index) => (
                <div
                  key={url}
                  onClick={() => handleSuggestionClick(url)}
                  className={`flex items-center gap-3 px-3 py-2 my-1 cursor-pointer transition-colors group ${
                    index === selectedSuggestionIndex
                      ? "bg-gray-100"
                      : "hover:bg-[#F8F9FC] mx-2 rounded-md"
                  }`}
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt=""
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <span className="text-[12px] 2xl:text-sm text-[#000000] truncate flex-1">
                    {url}
                  </span>

                  <UrlArrow className="w-2 h-2 xl:w-2 xl:h-2 2xl:w-2.5 2xl:h-2.5 4xl:w-3 4xl:h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          )}

          {/* URL Lock Notification */}
          {isUrlLocked && (
            <div
              className="mt-1 text-xs text-gray-500 flex items-center gap-1"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
              </svg>
              URL locked - feedback items added
            </div>
          )}
        </form>
      </div>
    ),
    [
      inputUrl,
      handleUrlInputChange,
      handleUrlInputKeyDown,
      handleUrlInputFocus,
      handleUrlSubmit,
      handleSuggestionClick,
      showUrlDropdown,
      filteredUrls,
      selectedSuggestionIndex,
      isCreatingFeedback,
      isUrlLocked,
    ],
  );

  // Header Navigation Component for fullscreen view
  const HeaderNavigation = useMemo(
    () => (
      <div className="flex items-center gap-5">
        <h1
          className="text-sm sm:text-base xl:text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl font-medium text-gray-900 truncate"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {feedbackName}
        </h1>

        <form
          onSubmit={handleUrlSubmit}
          className="w-80 sm:w-80 xl:w-[20rem] 2xl:w-[24rem] 3xl:w-[28rem] 4xl:w-[32rem] relative"
        >
          <div
            className={`flex items-center bg-white border border-[#E3E5EF] rounded-[4px] px-2 sm:px-2 xl:px-2 2xl:px-2 4xl:px-3 py-1 sm:py-1 xl:py-1.5 2xl:py-1.5 4xl:py-2 w-full focus-within:ring-[#E3E5EF] focus-within:border-[#E3E5EF] ${
              isUrlLocked ? "bg-gray-50 border-gray-200" : ""
            }`}
          >
            <img
              src="https://www.google.com/favicon.ico"
              alt=""
              className="w-3 h-3 sm:w-3 sm:h-3 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 4xl:w-5 4xl:h-5 mr-1.5 sm:mr-1.5 xl:mr-2 2xl:mr-2 4xl:mr-3 flex-shrink-0"
            />
            <input
              ref={urlInputRef}
              type="text"
              placeholder={isUrlLocked ? "URL locked" : "Enter URL"}
              value={inputUrl}
              onChange={handleUrlInputChange}
              onKeyDown={handleUrlInputKeyDown}
              onFocus={handleUrlInputFocus}
              className={`flex-1 bg-transparent border-none outline-none text-[10px] sm:text-[10px] xl:text-xs 2xl:text-xs 3xl:text-sm 4xl:text-base font-inter text-gray-800 placeholder-gray-400 min-w-0 ${
                isUrlLocked ? "cursor-not-allowed text-gray-500" : ""
              }`} 
              autoComplete="off"
              disabled={isCreatingFeedback || isUrlLocked}
              readOnly={isUrlLocked}
              title={
                isUrlLocked
                  ? "URL cannot be changed after adding feedback items"
                  : ""
              }
            />
            <button
              type="submit"
              className="ml-1.5 outline-none hover:opacity-70 transition-opacity disabled:opacity-50"
              aria-label={
                isUrlLocked ? "Reload current URL" : "Navigate to URL"
              }
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              disabled={isCreatingFeedback}
            >
              {isCreatingFeedback ? (
                <div className="w-3 h-3 sm:w-4 sm:h-4 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 animate-spin">
                  <svg
                    className="w-full h-full"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              ) : isUrlLocked ? (
                <Reload className="w-3 h-3 sm:w-4 sm:h-4 xl:w-5 xl:h-5 4xl:w-6 4xl:h-6 text-gray-400 flex-shrink-0" />
              ) : (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 xl:w-5 xl:h-5 4xl:w-6 4xl:h-6 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* URL Suggestions Dropdown for Header */}
          {showUrlDropdown && filteredUrls.length > 0 && !isUrlLocked && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
            >
              <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                Recently used
              </div>
              {filteredUrls.map((url, index) => (
                <div
                  key={url}
                  onClick={() => handleSuggestionClick(url)}
                  className={`flex items-center gap-3 px-3 py-2 my-1 cursor-pointer transition-colors group ${
                    index === selectedSuggestionIndex
                      ? "bg-gray-100"
                      : "hover:bg-[#F8F9FC] mx-2 rounded-md"
                  }`}
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt=""
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <span
                    className="text-[12px] 2xl:text-sm text-[#000000] truncate flex-1"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {url}
                  </span>

                  <UrlArrow className="w-2 h-2 xl:w-2 xl:h-2 2xl:w-2.5 2xl:h-2.5 4xl:w-3 4xl:h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          )}
        </form>
      </div>
    ),
    [
      inputUrl,
      handleUrlInputChange,
      handleUrlInputKeyDown,
      handleUrlInputFocus,
      handleUrlSubmit,
      handleSuggestionClick,
      showUrlDropdown,
      filteredUrls,
      selectedSuggestionIndex,
      isCreatingFeedback,
      isUrlLocked,
    ],
  );

  return (
    <div className="w-full h-full min-h-screen bg-white flex flex-col relative overflow-hidden">
      {/* ✅ ADD THIS ENTIRE SUCCESS MESSAGE COMPONENT */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p
                  className="text-sm font-medium text-green-800"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Delete Successful!
                </p>

                <p
                  className="text-sm text-green-700 mt-1"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {successMessageText}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowSuccessMessage(false)}
                  className="bg-green-50 rounded-md inline-flex text-green-400 hover:text-green-500 focus:outline-none"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UPDATED: Preview Panel with video thumbnail support and screen aspect ratio */}
      {!isCapturing && hoveredItemId &&
        (() => {
          const hoveredItem = allFeedbackItems.find(
            (item) => `${item.type}-${item.id}` === hoveredItemId,
          );

          if (!hoveredItem) return null;

          const hoveredIndex = allFeedbackItems.findIndex(
            (item) => `${item.type}-${item.id}` === hoveredItemId,
          );

          const hoveredElement = document.querySelector(
            `[data-item-index="${hoveredIndex}"]`,
          );
          const hoveredRect = hoveredElement?.getBoundingClientRect();

          if (!hoveredRect) return null;

          // Calculate preview dimensions based on screen aspect ratio
          const screenAspectRatio = window.innerWidth / window.innerHeight;
          const maxPreviewWidth = 420; // Base max width
          const previewWidth = Math.min(
            maxPreviewWidth,
            window.innerWidth * 0.25,
          );
          const previewHeight = previewWidth / screenAspectRatio;

          let finalTop = hoveredRect.top;
          const viewportHeight = window.innerHeight;
          const bottomPadding = 52;
          const previewBottom = finalTop + previewHeight;

          if (previewBottom > viewportHeight - bottomPadding) {
            const overflow = previewBottom - (viewportHeight - bottomPadding);
            finalTop = finalTop - overflow;
            if (finalTop < 40) finalTop = 40;
          }

          // Calculate responsive left position based on sidebar width
          // Both normal (ml-2) and fullscreen (left-2) sidebar start at 8px from viewport left
          const getLeftPosition = () => {
            const width = window.innerWidth;
            const sidebarLeft = 8; // ml-2 / left-2
            const gap = 4;
            let sidebarWidth;
            if (width >= 2300) {
              sidebarWidth = 214;
            } else if (width >= 1920) {
              sidebarWidth = 160;
            } else if (width >= 1536) {
              sidebarWidth = 138;
            } else if (width >= 1280) {
              sidebarWidth = 124;
            } else if (width >= 640) {
              sidebarWidth = 112;
            } else {
              sidebarWidth = 64;
            }
            return `${sidebarLeft + sidebarWidth + gap}px`;
          };

          return (
            <div
              className="fixed bg-white border-[1.5px] border-[#5A5A5A]/50 rounded-[8px] shadow-2xl overflow-hidden pointer-events-none"
              style={{
                animation: "fadeIn 0.2s ease-in-out",
                left: getLeftPosition(),
                top: `${finalTop}px`,
                width: `${previewWidth}px`,
                height: `${previewHeight}px`,
                maxHeight: "calc(100vh - 120px)",
                zIndex: 10000,
              }}
            >
              {hoveredItem.type === "screenshot" ? (
                <div className="relative h-full">
                  <div className=" h-full flex items-center justify-center">
                    <img
                      src={hoveredItem.annotatedImage}
                      alt="Preview"
                      className="w-full h-full  object-cover"
                    />
                  </div>
                </div>
              ) : (
                <div className="relative h-full">
                  {/* UPDATED: Show video thumbnail in preview if available */}
                  {hoveredItem.thumbnailUrl ? (
                    <div className="relative bg-gray-100 h-full flex items-center justify-center">
                      <img
                        src={hoveredItem.thumbnailUrl}
                        alt="Recording preview"
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                        <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-black ml-1"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-20 h-20 mx-auto bg-gray-700 rounded-full flex items-center justify-center mb-3">
                          <svg
                            className="w-10 h-10 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

      {/* Header */}
      <header className={`bg-white px-4  py-2 sm:py-3 xl:py-3 2xl:py-4 4xl:py-6 w-full ${isWorkspaceFullscreen ? "hidden" : ""}`}>
        <div className="flex items-center justify-between">
          {/* Left side - Home icon and Feedback name or Header Navigation */}
          <div className="flex items-center gap-4 flex-1 mr-4">
            {/* Home Icon Button */}
            <DynamicTooltip label="Go to Home (H)">
              <button
                onClick={handleGoToHome}
                className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 xl:w-10 xl:h-10 2xl:w-11 2xl:h-11 4xl:w-12 4xl:h-12 bg-[#EFF0F5] hover:bg-[#DFE1E8] rounded-[6px] transition-colors"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                <Home className="w-5 h-5 sm:w-6 sm:h-6 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 4xl:w-9 4xl:h-9" />
              </button>
            </DynamicTooltip>

            {isFullScreen ? (
              HeaderNavigation
            ) : (
              <div className="flex items-center gap-3">
                <h1 className="text-sm sm:text-base xl:text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl font-medium text-gray-900 truncate">
                  {feedbackName}
                </h1>

                <button
                  onClick={handleNewTabClick}
                  className="w-[333px] h-[40px] flex items-center px-3 py-1.5 bg-[#EFF0F5] rounded-lg hover:bg-[#E5E6EB] transition-colors cursor-pointer"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt=""
                    className="w-3 h-3 sm:w-4 sm:h-4 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 mr-1.5 sm:mr-2 xl:mr-2  4xl:mr-4 flex-shrink-0"
                  />
                  {/* Vertical Separator */}
                  <div className="w-px bg-[#B7BBCA] self-stretch my-1 mr-1.5 sm:mr-2 xl:mr-2  4xl:mr-4 "></div>
                  <span
                    className="text-sm 3xl:text-[14px] text-[#646569]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    New tab
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-2 sm:gap-2 xl:gap-2 2xl:gap-3 4xl:gap-4 flex-shrink-0">
            {!hasAddedFeedback && (
              <DynamicTooltip label="Delete Project">
                <button
                  className="text-red-500 hover:text-red-700 transition p-1 rounded"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  onClick={handleDeleteButtonClick}
                  disabled={isDeletingCard}
                >
                  <DeleteIcon className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 4xl:w-8 4xl:h-8" />
                </button>
              </DynamicTooltip>
            )}
            <Button
              variant="cancel"
              size="md"
              onClick={handleSaveDraft}
              disabled={!hasAddedFeedback}
              className={`text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl px-2 sm:px-3 xl:px-4 2xl:px-5 4xl:px-6 py-1 sm:py-1.5 xl:py-2 2xl:py-2.5 4xl:py-3 ${
                !hasAddedFeedback ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Save as draft
            </Button>

            <ViewModeDropdown
              totalFeedbackItems={totalFeedbackItems}
              hasAddedFeedback={hasAddedFeedback}
              onViewList={handleSaveAndExitWithLatestData}
              onShare={handleShareClick}
              isSharing={isSharing}
            />

            <div className="w-px h-6 bg-[#D7D9E2] flex-shrink-0" />

            <DynamicTooltip
              label={isWorkspaceFullscreen ? "Exit full screen (F)" : "Full screen (F)"}
              wrapperClassName="flex-shrink-0"
            >
              <button
                onClick={() => setIsWorkspaceFullscreen((prev) => !prev)}
                className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 xl:w-10 xl:h-10 2xl:w-11 2xl:h-11 4xl:w-12 4xl:h-12 hover:bg-[#EFF0F5] rounded-[6px] transition-colors"
              >
                {isWorkspaceFullscreen ? (
                  <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#192028]" />
                ) : (
                  <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#192028]" />
                )}
              </button>
            </DynamicTooltip>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex flex-1 min-h-0">
        {/* Left-edge visible handle — fullscreen only, full-height hover zone */}
        {isWorkspaceFullscreen && !showFloatingSidebar && !isRecordingActive && (
          <div
            className="fixed left-0 top-0 bottom-0 w-8 z-50 cursor-pointer group"
            onMouseEnter={() => setShowFloatingSidebar(true)}
            onClick={() => setShowFloatingSidebar(true)}
          >
            {/* Visual handle centered vertically — bar with arrow on it */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 flex items-center justify-center w-2 h-40 bg-[#D7D9E2] group-hover:bg-[#B7BBCA] rounded-r-full transition-colors duration-150">
              <svg className="w-2.5 h-2.5 text-gray-800 opacity-70 group-hover:opacity-100 transition-opacity duration-150" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        )}

        {/* Left Sidebar */}
        <aside
          ref={floatingSidebarRef}
          onMouseLeave={() => isWorkspaceFullscreen && setShowFloatingSidebar(false)}
          className={isWorkspaceFullscreen
            ? `fixed left-2 z-50 border border-[#D7D9E2] rounded-[8px] shadow-xl flex flex-col items-center w-16 sm:w-28 xl:w-[124px] 2xl:w-[138px] 3xl:w-40 4xl:w-[214px] overflow-hidden ${!toolboxPos ? "bottom-[60px] xl:bottom-[68px] 2xl:bottom-[76px]" : "bottom-2"}`
            : `ml-2 3xl:ml-3 w-16 sm:w-28 xl:w-[124px] 2xl:w-[138px] 3xl:w-40 4xl:w-[214px] bg-[#F7F7F7] border-x border-t border-[#D7D9E2] rounded-t-[8px] flex flex-col items-center relative h-full${hideSidebar ? " invisible" : ""}`
          }
          {...(isWorkspaceFullscreen ? { "data-feebdack-ui-overlay": "true" } : {})}
          style={isWorkspaceFullscreen ? {
            top: showFloatingTopBar ? '50px' : '8px',
            background: "#FFFEFECC",
            backdropFilter: "blur(4px)",
            transform: showFloatingSidebar ? "translateX(0)" : "translateX(-110%)",
            opacity: showFloatingSidebar ? 1 : 0,
            pointerEvents: showFloatingSidebar ? "auto" : "none",
            transition: showFloatingSidebar
              ? "top 200ms ease-out, bottom 300ms ease-in-out"
              : "transform 500ms ease-in-out, opacity 500ms ease-in-out, top 200ms ease-out, bottom 300ms ease-in-out",
            visibility: isRecordingActive ? 'hidden' as const : undefined,
          } : undefined}
        >
          {/* UPDATED: Feedback Items Container with video thumbnails */}
          <div className={`sidebar-scroll-wrapper relative w-full pt-1 flex-1 min-h-0`}>
          <div
            ref={sidebarItemsRef}
            onScroll={handleSidebarScroll}
            className={`flex flex-col items-center gap-1 w-full h-full pb-1 overflow-y-auto overflow-x-hidden sidebar-no-scrollbar`}
          >
            <style>{`
 .sidebar-no-scrollbar {
  overflow-y: auto;
  scrollbar-width: none;
}

.sidebar-no-scrollbar::-webkit-scrollbar {
  width: 0px;
  display: none;
}

.sidebar-scroll-thumb {
  opacity: 0;
  transition: opacity 0.2s;
}

.sidebar-scroll-wrapper:hover .sidebar-scroll-thumb {
  opacity: 1;
}
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* ✨ NEW: Smooth progress bar transition */
  .progress-bar-fill {
    transition: width 0.3s ease-out;
  }

  @keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-10px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(10px);
  }
}

.shake-animation {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
}

`}</style>

            {allFeedbackItems.map((item, index) => {
              const itemKey = `${item.type}-${item.id}`;
              const isHovered = hoveredItemId === itemKey;

              return (
                <div
                  key={itemKey}
                  onMouseEnter={() => setHoveredItemId(itemKey)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  data-item-index={index}
                  className={`relative group w-14 h-6 sm:w-[104px] sm:h-[52px] xl:w-[116px] xl:h-[60px] 2xl:w-[130px] 2xl:h-[64px] 3xl:w-[152px] 3xl:h-[80px] 4xl:w-[206px] 4xl:h-[106px] border border-gray-200 rounded-[4px] overflow-visible shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0`}
                  style={{
                    zIndex: isHovered ? 200 : "auto",
                  }}
                >
                  {item.type === "screenshot" ? (
                    <>
                      <img
                        src={item.annotatedImage}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-full object-cover rounded-[4px] pointer-events-none"
                      />
                      {/* ✅ REPLACE THE ENTIRE div.absolute BLOCK WITH THIS */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center gap-2">
                        {/* Edit Icon  */}
                        <div className="relative group/btn">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditScreenshot(item.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                          >
                            <EditPreview className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                          </button>
                        </div>

                        {/* Delete Icon */}
                        <div className="relative group/btn">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveScreenshot(item.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                          >
                            <Delete className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Existing thumbnail/play icon code stays the same */}
                      {item.thumbnailUrl ? (
                        <div
                          className="relative w-full h-full rounded-[4px] cursor-pointer"
                          onClick={() => handlePlayRecording(item.id)}
                        >
                          <img
                            src={item.thumbnailUrl}
                            alt={`Recording ${index + 1} thumbnail`}
                            className="w-full h-full object-cover pointer-events-none rounded-[4px]"
                            onError={(e) => {
                              console.warn(
                                `Failed to load thumbnail for ${item.id}`,
                              );
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-[4px]">
                            <div className="w-4 h-4 sm:w-4 sm:h-4 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 4xl:w-10 4xl:h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                              <svg
                                className="w-3 h-3 sm:w-3 sm:h-3 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6 text-black ml-0.5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-[4px] cursor-pointer"
                          onClick={() => handlePlayRecording(item.id)}
                        >
                          <div className="w-3 h-3 sm:w-4 sm:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 4xl:w-8 4xl:h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <svg
                              className="w-2 h-2 sm:w-2.5 sm:h-2.5 xl:w-3 xl:h-3 2xl:w-3.5 2xl:h-3.5 4xl:w-4 4xl:h-4 text-black ml-0.5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}

                      <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-70 transition-opacity">
                        <svg
                          className="w-2 h-2 sm:w-3 sm:h-3 text-gray-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 6h2v2H8zm0 4h2v2H8zm0 4h2v2H8zm4-8h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2z" />
                        </svg>
                      </div>

                      {/* ✅ REPLACE THIS ENTIRE DIV - ADD EDIT AND DELETE BUTTONS */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center gap-2">
                        {/* Edit Icon */}
                        <div className="relative group/btn">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditRecording(item.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                          >
                            <EditPreview className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                          </button>
                        </div>

                        {/* Delete Icon */}
                        <div className="relative group/btn">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveRecording(item.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-white w-6 h-6 sm:w-7 sm:h-7 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 4xl:w-12 4xl:h-12 flex items-center justify-center transition-all duration-200"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                          >
                            <Delete className="w-8 h-8 xl:w-14 xl:h-14 3xl:w-16 3xl:h-16" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            {loadingItems.map((loadingItem) => (
              <div
                key={`loading-${loadingItem.id}`}
                className="w-14 h-6 sm:w-[104px] sm:h-[52px] xl:w-[116px] xl:h-[60px] 2xl:w-[130px] 2xl:h-[64px] 3xl:w-[152px] 3xl:h-[80px] 4xl:w-[206px] 4xl:h-[106px] border border-gray-200 rounded-[4px] overflow-hidden shadow-sm flex-shrink-0 bg-gradient-to-r from-gray-100 to-gray-200"
                style={{
                  opacity: loadingItem.progress === 100 ? 0 : 1,
                  transition: "opacity 0.5s ease-out",
                }}
              >
                <div className="w-full h-full flex flex-col items-center justify-center p-2">
                  {/* Progress Bar with actual percentage */}
                  <div className="w-full h-1 bg-gray-300 rounded-full overflow-hidden ">
                    <div
                      className="h-full bg-[#1E1F26] rounded-full progress-bar-fill"
                      style={{
                        width: `${loadingItem.progress}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {totalFeedbackItems === 0 && loadingItems.length === 0 && (
              <div className="w-14 h-6 sm:w-[104px] sm:h-[52px] xl:w-[116px] xl:h-[60px] 2xl:w-[130px] 2xl:h-[64px] 3xl:w-[152px] 3xl:h-[80px] 4xl:w-[206px] 4xl:h-[106px] bg-[#F8F9FC] rounded-[4px] border border-gray-200 shadow-sm flex-shrink-0" />
            )}
          </div>
          {/* Custom overlay scrollbar thumb */}
          <div
            ref={sidebarThumbRef}
            className="sidebar-scroll-thumb absolute right-[2px] top-0 w-[4px] rounded-full bg-[#647178] cursor-default"
            onMouseDown={handleThumbMouseDown}
            style={{ height: "40px" }}
          />
          </div>

          {/* ScreenshotManager Section - Normal mode only */}
          {!isWorkspaceFullscreen && (
            <div
              className="w-full bg-[#F7F7F7] border-t border-[#D7D9E2] mt-auto"
              data-tour="screenshot-tool"
            >
              <div className="w-full py-2 flex items-center justify-center bg-white">
                <ScreenshotManager
                  onToolClick={handleToolClick}
                  onScreenshotSubmit={handleScreenshotSubmit}
                  onRecordingSubmit={handleRecordingSubmit}
                  onCaptureEnd={handleCaptureEnd}
                  uniqueFeedbackId={feedbackId}
                  isMuted={isMuted}
                  cardId={cardId}
                  hasUrl={!!(showIframe && currentIframeUrl)}
                />
              </div>
            </div>
          )}
        </aside>

        {/* Toast notification */}
        {fsToast.visible && (
          <div className="fixed top-4 right-4 z-[9999] max-w-md animate-slide-in">
            <div className={`${fsToast.type === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border rounded-lg p-4 shadow-lg`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {fsToast.type === "success" ? (
                    <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <p className={`text-sm font-medium ${fsToast.type === "success" ? "text-green-800" : "text-red-800"}`}>
                    {fsToast.type === "success" ? "Success" : "Error"}
                  </p>
                  <p className={`text-sm mt-1 ${fsToast.type === "success" ? "text-green-700" : "text-red-700"}`}>
                    {fsToast.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => setFsToast((t) => ({ ...t, visible: false }))}
                    className={`${fsToast.type === "success" ? "bg-green-50 text-green-400 hover:text-green-500" : "bg-red-50 text-red-400 hover:text-red-500"} rounded-md inline-flex focus:outline-none`}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Floating top bar — revealed when cursor touches top edge in fullscreen */}
        {isWorkspaceFullscreen && (
          <>
            {/* invisible top-edge hover trigger zone */}
            <div
              className="fixed top-0 left-0 right-0 h-3 z-[60]"
              onMouseEnter={() => setShowFloatingTopBar(true)}
            />
            <div
              className="fixed top-0 left-0 right-0 z-[55]"
              onMouseLeave={() => setShowFloatingTopBar(false)}
              data-feebdack-ui-overlay="true"
              style={{
                transform: showFloatingTopBar ? "translateY(0)" : "translateY(-110%)",
                opacity: showFloatingTopBar ? 1 : 0,
                transition: showFloatingTopBar
                  ? "none"
                  : "transform 500ms ease-in-out, opacity 500ms ease-in-out",
                ...(isRecordingActive ? { visibility: 'hidden' as const } : {}),
              }}
            >
              <div className="bg-white/95 backdrop-blur-md border-b border-[#D7D9E2] border-opacity-50 px-3  xl:px-4 py-1.5 flex items-center justify-between gap-3">
                {/* Left: Home button + feedback name + URL bar (same flow as normal header) */}
                <div className="flex items-center gap-3 flex-1 min-w-0 mr-4">
                  <DynamicTooltip label="Go to Home (H)" wrapperClassName="flex-shrink-0">
                    <button
                      onClick={handleGoToHome}
                      className="flex items-center justify-center w-8 h-8 bg-[#EFF0F5] hover:bg-[#DFE1E8] rounded-[6px] transition-colors"
                    >
                      <Home className="w-5 h-5" />
                    </button>
                  </DynamicTooltip>
                  {HeaderNavigation}
                </div>

                {/* Right: action buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!hasAddedFeedback && (
                    <DynamicTooltip label="Delete Project">
                      <button
                        className="text-red-500 hover:text-red-700 transition p-1 rounded"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        onClick={handleDeleteButtonClick}
                        disabled={isDeletingCard}
                      >
                        <DeleteIcon className="w-5 h-5" />
                      </button>
                    </DynamicTooltip>
                  )}
                  <Button
                    variant="cancel"
                    size="md"
                    onClick={handleSaveDraft}
                    disabled={!hasAddedFeedback}
                    className={`text-xs 3xl:text-xs px-3 py-2 rounded-[4px] ${!hasAddedFeedback ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Save as draft
                  </Button>
                  <ViewModeDropdown
                    totalFeedbackItems={totalFeedbackItems}
                    hasAddedFeedback={hasAddedFeedback}
                    onViewList={handleSaveAndExitWithLatestData}
                    onShare={handleShareClick}
                    isSharing={isSharing}
                    compact
                  />
                  <div className="w-px h-6 bg-[#D7D9E2] flex-shrink-0" />
                  <DynamicTooltip label="Exit full screen (Esc)" wrapperClassName="flex-shrink-0">
                    <button
                      onClick={() => setIsWorkspaceFullscreen(false)}
                      className="flex items-center justify-center w-8 py-2  hover:bg-[#EFF0F5] rounded-[4px] transition-colors"
                    >
                      <Minimize2 className="w-4 h-4 text-[#192028]" />
                    </button>
                  </DynamicTooltip>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Floating Toolbox — separate, always visible in fullscreen */}
        {isWorkspaceFullscreen && (
          <div
            ref={toolboxRef}
            className="fixed z-[45]"
            data-tour="screenshot-tool-fullscreen"
            data-feebdack-ui-overlay="true"
            style={
              toolboxPos
                ? {
                    left: toolboxPos.left,
                    top: toolboxPos.top,
                    cursor: isDrawingMode ? "default" : isAnyPreviewOpen || showEditPreview || showEditRecordingPreview ? "auto" : "grab",
                  }
                : { bottom: 8, left: 8, cursor: isDrawingMode ? "default" : isAnyPreviewOpen || showEditPreview || showEditRecordingPreview ? "auto" : "grab" }
            }
            onMouseDown={(e) => {
              if (isDrawingMode || isAnyPreviewOpen || showEditPreview || showEditRecordingPreview) return;
              const target = e.target as HTMLElement;
              if (target.closest("button") || target.closest("input") || target.closest("textarea") || target.closest("select") || target.closest("[contenteditable]")) return;
              const el = toolboxRef.current;
              if (!el) return;
              const rect = el.getBoundingClientRect();
              // Disable transition and hint GPU layer for smooth drag
              el.style.transition = 'none';
              el.style.willChange = 'transform';
              el.style.cursor = 'grabbing';
              // Grabbing cursor persists over iframes and overlays
              document.body.style.cursor = 'grabbing';
              document.body.style.userSelect = 'none';
              toolboxDragRef.current = {
                isDragging: true,
                startX: e.clientX,
                startY: e.clientY,
                startLeft: rect.left,
                startTop: rect.top,
              };
              e.preventDefault();
            }}
          >
            <ScreenshotManager
              onToolClick={handleToolClick}
              onScreenshotSubmit={handleScreenshotSubmit}
              onRecordingSubmit={handleRecordingSubmit}
              onCaptureEnd={handleCaptureEnd}
              uniqueFeedbackId={feedbackId}
              isMuted={isMuted}
              cardId={cardId}
              hasUrl={!!(showIframe && currentIframeUrl)}
              compact
              isFullscreen={isWorkspaceFullscreen}
            />
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col">
          <div
            className={`flex-1 flex flex-col relative ${
              isWorkspaceFullscreen
                ? ""
                : `border border-gray-200 ${!isFullScreen ? "rounded-t-[8px] mx-4" : "rounded-t-[8px] mx-4"}`
            }`}
          >
            {showIframe && currentIframeUrl ? (
              iframeLoadError ? (
                <IframeErrorPage
                  errorType={iframeErrorType}
                  url={currentIframeUrl}
                  onRetry={() => {
                    setIframeLoadError(false);
                    setIframeErrorType(null);
                    setIframeLoading(true);
                    setIframeKey((prev) => prev + 1);
                  }}
                />
              ) : (
                <>
                  {iframeLoading && (
                    <div className="flex-1 flex flex-col items-center justify-center w-full h-full bg-white rounded-t-[8px]">
                      <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
                      <p className="text-sm text-gray-400 mt-3">Loading preview...</p>
                    </div>
                  )}
                  <iframe
                    key={iframeKey}
                    ref={iframeRef}
                    src={currentIframeUrl}
                    title="Website Mirror"
                    className={`w-full h-full border-1 border-gray-200 overflow-hidden ${isWorkspaceFullscreen ? "" : "rounded-t-[8px]"} ${iframeLoading ? "hidden" : ""}`}
                    scrolling="auto"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                    onError={() => {
                      iframeLoadedRef.current = true;
                      setIframeErrorType('network');
                      setIframeLoadError(true);
                      setIframeLoading(false);
                    }}
                    onLoad={(e) => {
                      iframeLoadedRef.current = true;
                      const iframe = e.target as HTMLIFrameElement;
                      try {
                        const doc = iframe.contentDocument;
                        // Empty document means the browser silently blocked the iframe
                        // (X-Frame-Options: DENY/SAMEORIGIN or CSP frame-ancestors)
                        if (doc && (!doc.body || doc.body.innerHTML.trim() === '')) {
                          setIframeErrorType('blocked');
                          setIframeLoadError(true);
                        } else if (doc) {
                          // Same-origin: try to read build version from window globals or meta tags
                          const win = iframe.contentWindow as any;
                          const ver = win?.FEEBDACK_BUILD_VERSION
                            || win?.APP_VERSION
                            || win?.__BUILD_VERSION__
                            || win?.REACT_APP_VERSION
                            || doc.querySelector('meta[name="feebdack-build-version"]')?.getAttribute('content')
                            || doc.querySelector('meta[name="app-version"]')?.getAttribute('content');
                          if (ver && !iframeContextRef.current.build_version) {
                            iframeContextRef.current.build_version = String(ver);
                          }
                        }
                      } catch {
                        // SecurityError on a cross-origin iframe — the backend pre-flight
                        // check already confirmed this URL can embed, so this is a normal
                        // cross-origin page. Just clear the spinner.
                        setIframeLoading(false);
                      }
                      // Ask the iframe SDK for its context (works cross-origin when SDK handles feebdack_request_context)
                      try {
                        iframe.contentWindow?.postMessage({ type: 'feebdack_request_context' }, '*');
                      } catch {}
                    }}
                  />
                </>
              )
            ) : (
              <div className="flex-1 flex  bg-[#F9FAFF] rounded-t-[8px] relative ">
                <div className="flex flex-col items-center justify-center w-full mb-40">
                  <CluvaLogo className="w-36 h-36 4xl:w-44 4xl:h-44 -mb-6" />
                  <div
                    className={`w-full max-w-2xl 4xl:max-w-4xl ${
                      isShaking ? "shake-animation" : ""
                    }`}
                  >
                    {BrowserNavigation}
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Popup */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
                  <div className="px-4 py-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mt-2 mb-2">
                      <h3
                        className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Are you sure?
                      </h3>
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-6" />

                    {/* Description */}
                    <p
                      className="text-[#070707] text-sm 3xl:text-[16px] mb-6"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      This action cannot be undone. All values associated with
                      this will be lost.
                    </p>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-4" />

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="cancel"
                        size="lg"
                        className="w-full sm:w-auto text-sm"
                        onClick={handleDeleteCancel}
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="danger"
                        size="lg"
                        className="w-full sm:w-auto text-sm"
                        onClick={handleDeleteConfirm}
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Card Confirmation Popup */}
            {showDeleteCardConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
                  <div className="px-4 py-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mt-2 mb-2">
                      <h3
                        className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Discard project?
                      </h3>
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-6" />

                    {/* Description */}
                    <p
                      className="text-[#070707] text-sm 3xl:text-[16px] mb-6"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      Are you sure you want to discard the project? This step
                      will not be revert back.
                    </p>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-4" />

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="cancel"
                        size="lg"
                        className="w-full sm:w-auto text-sm"
                        onClick={() => setShowDeleteCardConfirm(false)}
                        disabled={isDeletingCard}
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Continue
                      </Button>
                      <Button
                        variant="danger"
                        size="lg"
                        className="w-full sm:w-auto text-sm"
                        onClick={handleDeleteCard}
                        disabled={isDeletingCard}
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        {isDeletingCard ? "Deleting..." : "Yes, Discard"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Screenshot Edit Preview */}
            {showEditPreview && editingScreenshot && (
              <ScreenshotPreviewComponent
                preview={{
                  dataUrl:
                    editingScreenshot.annotatedImage ||
                    editingScreenshot.originalImage,
                  area: {
                    startX: 0,
                    startY: 0,
                    endX: 0,
                    endY: 0,
                  },
                  actualWidth: 0,
                  actualHeight: 0,
                }}
                isLoading={false}
                onClose={() => {
                  setShowEditPreview(false);
                  setEditingScreenshot(null);
                }}
                onDownload={() => {
                  // Optional: Add download functionality if needed
                }}
                onSubmit={handleEditSubmit}
                uniqueFeedbackId={
                  editingScreenshot.uniqueFeedbackId || feedbackId
                }
                isEditMode={true}
                existingFeedback={editingScreenshot.feedback || ""}
                existingAudioUrl={editingScreenshot.audioUrl}
                existingAudioBlob={editingScreenshot.audioBlob}
                existingAudioFilename={editingScreenshot.audioFilename}
              />
            )}

            {/* Recording Edit Preview */}
            {showEditRecordingPreview && editingRecording && (
              <ScreenRecordingPreview
                preview={{
                  dataUrl: editingRecording.dataUrl,
                  blob:
                    editingRecording.recordingBlob ||
                    new Blob([], { type: "video/mp4" }), // ✅ FIX: Provide fallback Blob
                  duration: editingRecording.duration,
                  timestamp: editingRecording.timestamp,
                }}
                onClose={() => {
                  setShowEditRecordingPreview(false);
                  setEditingRecording(null);
                }}
                onDownload={() => {
                  console.log("Download recording:", editingRecording.id);
                }}
                onSubmit={handleEditRecordingSubmit}
                uniqueFeedbackId={
                  editingRecording.uniqueFeedbackId || feedbackId
                }
                isEditMode={true}
                existingFeedback={editingRecording.feedback || ""}
                existingRecordingBlob={editingRecording.recordingBlob} // ✅ This is still optional, which is fine
                existingDataUrl={editingRecording.dataUrl}
                existingThumbnailUrl={editingRecording.thumbnailUrl}
              />
            )}
          </div>
        </main>
      </div>
      <AppTour
        isOpen={showAppTour}
        onClose={handleCloseTour}
        steps={tourSteps}
      />
      {/* ✅ NEW: Submit Feedback Confirmation Popup */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mt-2 mb-2">
                <h3
                  className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Ready to share?
                </h3>
               
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-6" />

              {/* Description */}
              <p
                className="text-[#070707] text-sm 3xl:text-[16px]  mb-6 "
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Once shared, you won't be able to add new feedback or make any
                changes in this project.
              </p>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-4" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="cancel"
                  size="lg"
                   className="w-full sm:w-auto text-sm"
                  onClick={handleSubmitCancel}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleSubmitConfirm}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Yes, I am ready
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Share Feedback Popup */}
      <ShareFeedbackPopup
        isOpen={isSharePopupOpen}
        onClose={() => setIsSharePopupOpen(false)}
        title={`Share "${feedbackName}" with developers`}
        totalFeedback={totalFeedbackItems}
        onShare={handleShareEmails}
        onCancel={() => setIsSharePopupOpen(false)}
        cardId={cardId}
      />


    </div>
  );
};

export default FeedbackWorkspace;
